import type { Translations } from './types';

export const en: Translations = {
  // Common
  'common.loading': 'Loading…',
  'common.prev': 'Prev',
  'common.next': 'Next',
  'common.cancel': 'Cancel',
  'common.back': 'Back',
  'common.search': 'Search',
  'common.close': 'Close',
  'common.exit': 'Exit',
  'common.noData': 'No data',
  'common.failedToLoad': 'Failed to load',
  'common.retry': 'Retry',
  'common.reloadPage': 'Reload Page',
  'common.beta': 'Beta',

  // Auth
  'auth.subtitle': 'Kart Racing Telemetry Platform',
  'auth.signInDescription': 'Sign in to access telemetry data.',
  'auth.signInWithGoogle': 'Sign in with Google',
  'auth.signInWithApple': 'Sign in with Apple',
  'auth.or': 'or',
  'auth.signingIn': 'Signing in…',
  'auth.signOut': 'Sign Out',

  // OAuth Consent
  'oauth.authorizeTitle': 'Authorize {name}',
  'oauth.authorizeSubtitle': '{name} wants access to your kart.win telemetry account.',
  'oauth.permissionsTitle': 'Requested permissions',
  'oauth.permSessions': 'Read all sessions, laps, and telemetry channels',
  'oauth.permAnalysis': 'Read computed metrics and comparisons',
  'oauth.permSetups': 'Read kart setups and track configurations',
  'oauth.signedIn': 'Signed in',
  'oauth.mustSignInFirst': 'Please sign in to authorize this application',
  'oauth.authorizeButton': 'Authorize Application',
  'oauth.missingParams': 'Missing required OAuth authorization parameters',

  // Settings
  'settings.title': 'Settings',
  'settings.account': 'Account',
  'settings.profile': 'Profile',
  'settings.displayName': 'Display Name',
  'settings.saveName': 'Save Name',
  'settings.nameSaved': 'Name updated successfully',
  'settings.linkedAccounts': 'Linked Accounts',
  'settings.linkGoogle': 'Link Google Account',
  'settings.linkApple': 'Link Apple Account',
  'settings.unlink': 'Unlink',
  'settings.linked': 'Linked',
  'settings.notLinked': 'Not linked',
  'settings.cannotUnlinkOnlyProvider': 'At least one login method must remain linked.',
  'settings.language': 'Language',
  'settings.userId': 'User ID',
  'settings.signOut': 'Sign Out',
  'settings.connectedApps': 'Connected Applications',
  'settings.connectedAppsDesc': 'Manage AI assistants, IDEs, and tools authorized to access your telemetry data via MCP.',
  'settings.noConnectedApps': 'No connected applications yet. Authorize an MCP client (such as Claude Desktop or Cursor) to see it here.',
  'settings.authorizedOn': 'Authorized',
  'settings.lastUsed': 'Last Used',
  'settings.neverUsed': 'Never',
  'settings.revoke': 'Revoke',
  'settings.revokeAppConfirm': 'Are you sure you want to revoke access for this application? Any active sessions will immediately stop working.',
  'settings.appRevokedSuccess': 'Application access revoked successfully',
  'settings.copyKey': 'Copy Snippet',
  'settings.keyCopied': 'Copied to clipboard',
  'settings.mcpConfigSnippet': 'MCP Client Configuration',
  'settings.mcpConfigDesc': 'Add this configuration to your Claude Desktop (claude_desktop_config.json) or Cursor MCP settings. Authentication will complete automatically via your browser:',
  'settings.deleteAccount': 'Delete Account',
  'settings.deleteAccountDesc': 'Permanently delete your account and all associated sessions, telemetry data, saved analyses, equipment, kart setups, and sharing entries.',
  'settings.deleteAccountConfirmTitle': 'Delete Account Permanently?',
  'settings.deleteAccountConfirmDesc': 'This action cannot be undone. All of your owned sessions, telemetry data, saved analyses, equipment, kart setups, teams, and shared session entries will be immediately and permanently deleted.',
  'settings.deleteAccountConfirmPrompt': 'To confirm, type "{phrase}" in the box below:',
  'settings.deleteAccountConfirmPhrase': 'delete my account',
  'settings.deleteAccountConfirmButton': 'Delete My Account',

  // Dashboard
  'dashboard.overview': 'Overview',
  'dashboard.subtitle': 'Telemetry & Lap Data',
  'dashboard.recentSessions': 'Recent Sessions',
  'dashboard.total': '{n} total',
  'dashboard.failedToLoadDashboard': 'Failed to load dashboard',
  'dashboard.noDataAvailable': 'No data available',

  // Stats
  'stats.sessions': 'Sessions',
  'stats.laps': 'Laps',
  'stats.trackTime': 'Track Time',
  'stats.tracks': 'Tracks',

  // Search
  'search.placeholder': 'Search sessions… (e.g. name:WOHLEN laps:>=5)',
  'search.button': 'Search',
  'search.syntaxHelp': 'Query syntax help',
  'search.title': 'Search Query Syntax',
  'search.subtitle': 'Filter sessions using field:value pairs. Multiple terms are combined with AND.',
  'search.closeDialog': 'Close dialog',
  'search.operatorsTitle': 'Operators & Logic',
  'search.examplesTitle': 'Example Queries',
  'search.numericComparisons': 'Numeric comparisons:',
  'search.range': 'Range:',
  'search.rangeDesc': '— matches values between 5 and 20 inclusive.',
  'search.booleanLogic': 'Boolean logic:',
  'search.booleanLogicDesc': 'Terms are implicitly joined with',
  'search.quotes': 'Quotes:',
  'search.quotesDesc': 'Wrap values with spaces in double quotes, e.g.',

  // Filter UI
  'filter.add': 'Add filter',
  'filter.allActive': 'All filters active',
  'filter.clearAll': 'Clear all',
  'filter.clear': 'Clear',
  'filter.apply': 'Apply',
  'filter.cancel': 'Cancel',
  'filter.condition': 'Condition',
  'filter.presets': 'Presets',
  'filter.customRange': 'Custom Range',
  'filter.from': 'From',
  'filter.to': 'To',
  'filter.min': 'Min',
  'filter.max': 'Max',
  'filter.value': 'Value',
  'filter.searchOptions': 'Search options...',
  'filter.noOptions': 'No options found',
  'filter.loadingOptions': 'Loading options...',

  // Search fields
  'search.field.bareText': '(bare text)',
  'search.field.bareTextDesc': 'Matches track name or driver name containing the text.',
  'search.field.name': 'name',
  'search.field.nameDesc': 'Matches track name or driver name containing the value.',
  'search.field.driver': 'driver',
  'search.field.driverDesc': 'Matches driver name containing the value.',
  'search.field.laps': 'laps',
  'search.field.lapsDesc': 'Filter by completed lap count. Supports =, >, >=, <, <= and ranges.',
  'search.field.temp': 'temp',
  'search.field.tempDesc': 'Filter by weather temperature (°C). Supports comparison operators and ranges.',
  'search.field.season': 'season',
  'search.field.seasonDesc': 'Filter by season year (extracted from session date). Supports comparison operators.',
  'search.field.from': 'from',
  'search.field.fromDesc': 'Sessions on or after a date. Accepts YYYY-MM-DD or relative: -7d, -2w, -3m, -1y.',
  'search.field.to': 'to',
  'search.field.toDesc': 'Sessions on or before a date. Same formats as "from".',

  // Search examples
  'search.example.wohlenLaps': 'Sessions at Wohlen with 5+ laps',
  'search.example.yanick30d': "Yanick's sessions from the last 30 days",
  'search.example.tempLaps': 'Sessions between 15–25 °C with 3+ laps',
  'search.example.wohlenMagny': 'Wohlen or Magny sessions from the last year',

  // Session list
  'sessionList.date': 'Date',
  'sessionList.time': 'Time',
  'sessionList.track': 'Track',
  'sessionList.sessions': 'Sessions',
  'sessionList.driver': 'Driver',
  'sessionList.laps': 'Laps',
  'sessionList.best': 'Best',
  'sessionList.weather': 'Weather',
  'sessionList.lapsUnit': '{n} laps',
  'sessionList.sessionCount': '{count} sessions',
  'sessionList.sessionCountSingular': '1 session',
  'sessionList.noSessions': 'No sessions found. Try a different search.',

  // Track bests
  'trackBests.title': 'Track Records',

  // Session detail
  'session.title': 'Session',
  'session.failedToLoad': 'Failed to load session',
  'session.noData': 'No session data',
  'session.chipOverview': 'Overview',
  'session.chipDiff': 'Diff',
  'session.tabWeather': 'Weather',
  'session.tabLaps': 'Laps',
  'session.tabSetup': 'Setup',
  'session.tabManage': 'Manage',
  'session.dangerZone': 'Danger Zone',
  'session.deleteDescription': 'Permanently delete this session, all recorded laps, and telemetry data. This action cannot be undone.',
  'session.delete': 'Delete Session',
  'session.deleteConfirmTitle': 'Delete Session',
  'session.deleteConfirmMessage': 'Are you sure you want to delete this session? This action cannot be undone and will permanently remove all lap and telemetry data.',
  'session.deleteSuccess': 'Session deleted successfully',
  'session.deleteFailed': 'Failed to delete session',
  'session.deleting': 'Deleting session...',

  // Session meta
  'sessionMeta.date': 'Date',
  'sessionMeta.driver': 'Driver',
  'sessionMeta.track': 'Track',
  'sessionMeta.laps': 'Laps',
  'sessionMeta.device': 'Device',
  'sessionMeta.status': 'Status',
  'sessionMeta.tyres': 'Tyres',
  'sessionMeta.tyreAge': '{n} laps',
  'sessionMeta.fresh': 'Fresh',

  // Weather
  'weather.wind': 'Wind',
  'weather.humidity': 'Humidity',
  'weather.cloudCover': 'Cloud Cover',
  'weather.precipitation': 'Precipitation',
  'weather.pressure': 'Pressure',
  'weather.feels': 'Feels',
  'weather.clear': 'Clear',
  'weather.partlyCloudy': 'Partly Cloudy',
  'weather.overcast': 'Overcast',
  'weather.fog': 'Fog',
  'weather.drizzle': 'Drizzle',
  'weather.rain': 'Rain',
  'weather.freezingRain': 'Freezing Rain',
  'weather.snow': 'Snow',
  'weather.rainShowers': 'Rain Showers',
  'weather.snowShowers': 'Snow Showers',
  'weather.thunderstorm': 'Thunderstorm',
  'weather.unknown': 'Unknown',

  // Lap table
  'lapTable.title': 'Lap Times',
  'lapTable.theoLap': 'Fastest Lap',
  'lapTable.aggregation': 'Aggregation',
  'lapTable.compare': 'Compare',
  'lapTable.lapTime': 'Lap Time',
  'lapTable.delta': 'Delta',
  'lapTable.maxRpm': 'Max RPM',
  'lapTable.minRpm': 'Min RPM',
  'lapTable.maxSpeed': 'Max Speed',
  'lapTable.minSpeed': 'Min Speed',
  'lapTable.tyreAge': 'Tyre Age',

  // Diff chart
  'diffChart.outlierLap': 'Irrelevant',
  'diffChart.deltaToBest': 'Delta to Best',

  // Lap analysis
  'lapAnalysis.title': 'Lap {n} Analysis',
  'lapAnalysis.resetZoom': 'Reset Zoom',
  'lapAnalysis.speed': 'Speed',
  'lapAnalysis.acceleration': 'Acceleration',
  'lapAnalysis.rpm': 'RPM',
  'lapAnalysis.gForce': 'G-Force',
  'lapAnalysis.trackMap': 'Track Map',
  'lapAnalysis.failedToLoad': 'Failed to load telemetry',
  'lapAnalysis.noTelemetry': 'No telemetry data for this lap.',

  // Lap info
  'lapInfo.lapTime': 'Lap Time',
  'lapInfo.maxRpm': 'Max RPM',
  'lapInfo.minRpm': 'Min RPM',
  'lapInfo.maxSpeed': 'Max Speed',
  'lapInfo.maxTemp': 'Max Temp',
  'lapInfo.gpsOffset': 'GPS Offset',
  'lapInfo.gpsOffsetTooltip': 'Corrected GPS drift: {east} E, {north} N',

  // Navigation
  'nav.data': 'Data',
  'nav.home': 'Sessions',
  'nav.tools': 'Tools',
  'nav.compare': 'Compare',
  'nav.drivers': 'Drivers',
  'nav.tracks': 'Tracks',
  'nav.settings': 'Settings',

  // Analysis
  'analysis.title': 'Analysis Tools',
  'analysis.subtitle': 'Deep dive into your telemetry data with specialized analysis tools.',
  'analysis.sector.title': 'Sector Analysis',
  'analysis.sector.desc': 'Compare lap sectors across multiple sessions to find theoretical best laps and analyze consistency.',


  // Braking Analysis
  'analysis.braking.title': 'Braking Analysis',
  'analysis.braking.desc': 'Analyze braking points, deceleration zones, G-G friction circles, and trail-braking efficiency.',
  'braking.title': 'Braking Analysis',
  'braking.desc': 'Analyze braking points, deceleration zones, G-G friction circles, and trail-braking efficiency.',
  'braking.wizardChooseTrack': 'Choose Track',
  'braking.wizardSelect': 'Select Sessions',
  'braking.wizardResults': 'Results',
  'braking.noTracksWithCorners': 'No tracks with configured track shape and corners available.',
  'braking.noSessionsForTrack': 'No sessions found for track',
  'braking.noBrakingZones': 'No braking zones detected for this track.',
  'braking.selectZone': 'Select Braking Zone',
  'braking.zone': 'Braking Zone',
  'braking.zones': 'Braking Zones',
  'braking.brakePoint': 'Braking Point',
  'braking.apex': 'Apex',
  'braking.exit': 'Exit',
  'braking.distance': 'Braking Distance',
  'braking.duration': 'Braking Duration',
  'braking.initialSpeed': 'Initial Speed',
  'braking.apexSpeed': 'Min Speed',
  'braking.minSpeed': 'Min Speed',
  'braking.brakeRelease': 'Brake Release',
  'braking.exitSpeed': 'Exit Speed',
  'braking.speedDrop': 'Speed Drop',
  'braking.peakDecel': 'Peak Deceleration',
  'braking.peakLateral': 'Peak Lateral Grip',
  'braking.trailScore': 'Trail-Braking Score',
  'braking.trailScoreDesc': 'Measure of friction circle utilization while transitioning from longitudinal braking to lateral cornering.',
  'braking.scoreOptimal': 'Optimal',
  'braking.scoreGood': 'Good',
  'braking.scoreNeedsWork': 'Needs Work',
  'braking.sessionMedian': 'Session Median',
  'braking.selectedLap': 'Selected Lap',
  'braking.speedTransition': 'Speed Transition',
  'braking.decelAndGrip': 'Deceleration & Grip',
  'braking.trailScoreAdviceOptimal': 'Excellent brake tapering into corner entry. High friction circle utilization.',
  'braking.trailScoreAdviceGood': 'Smooth transition. Opportunity to trail brake slightly deeper into the corner entry.',
  'braking.trailScoreAdviceNeedsWork': 'Abrupt brake release before steering. Focus on progressive brake release while turning in.',
  'braking.overallHeader': 'Overall Braking Performance',
  'braking.overallHeaderDesc': 'Track-wide trail-braking efficiency and corner consistency across all selected laps and sessions.',
  'braking.perCornerHeader': 'Per-Corner Analysis',
  'braking.perCornerHeaderDesc': 'Detailed kinematics, G-G friction circle, and telemetry traces for the selected braking zone.',
  'braking.overallScore': 'Overall Trail-Braking Score',
  'braking.overallScoreDesc': 'Mean friction circle utilization across all corners and laps',
  'braking.mostConsistentCorner': 'Most Consistent Corner',
  'braking.leastConsistentCorner': 'Least Consistent Corner',
  'braking.consistency': 'Consistency',
  'braking.lapTimeline': 'Lap-by-Lap Score Timeline',
  'braking.sessionComparison': 'Session Comparison',
  'braking.selectCornerHint': 'Click to inspect corner',
  'braking.stdDevLabel': 'StdDev',
  'braking.ggDiagram': 'G-G Diagram',
  'braking.frictionCircle': 'Friction Circle',
  'braking.telemetryChart': 'Braking Telemetry',
  'braking.speed': 'Speed',
  'braking.gLong': 'Longitudinal G',
  'braking.gLat': 'Lateral G',
  'braking.gTotal': 'Combined G',
  'braking.allLaps': 'laps',
  'braking.compareLap': 'Compare',
  'braking.comparisonLap': 'Comparison',
  'braking.clearComparison': 'Clear comparison',
  'braking.lapTime': 'Lap Time',
  'braking.driver': 'Driver',
  'braking.lap': 'Lap',
  'braking.lapSpeedTable': 'Braking Laps Breakdown',
  'braking.lapsAnalyzed': 'laps analyzed',
  'braking.legendDecel': 'Straight Decel',
  'braking.legendTrail': 'Trail-Braking',
  'braking.legendApex': 'Apex',
  'braking.legendExit': 'Exit Throttle',

  // Corner Speed Analysis
  'analysis.corner.title': 'Corner Speed Analysis',
  'analysis.corner.desc': 'Measure and compare corner entrance, apex, and exit speeds across laps and sessions.',
  'corner.title': 'Corner Speed Analysis',
  'corner.wizardChooseTrack': 'Choose Track',
  'corner.wizardSelect': 'Select Sessions',
  'corner.wizardResults': 'Results',
  'corner.entrance': 'Entrance',
  'corner.apex': 'Apex',
  'corner.exit': 'Exit',
  'corner.entranceSpeed': 'Entrance Speed',
  'corner.apexSpeed': 'Apex Speed',
  'corner.exitSpeed': 'Exit Speed',
  'corner.speedGain': 'Speed Gain',
  'corner.turn': 'Turn',
  'corner.leftTurn': 'Left Turn',
  'corner.rightTurn': 'Right Turn',
  'corner.avgSpeed': 'Average Speed',
  'corner.maxSpeed': 'Max Speed',
  'corner.minSpeed': 'Min Speed',
  'corner.fastest': 'Fastest',
  'corner.slowest': 'Slowest',
  'corner.spread': 'Spread',
  'corner.deltaToBest': 'Δ to Best',
  'corner.deltaToAvg': 'Δ to Avg',
  'corner.allCornersOverview': 'All Corners Overview',
  'corner.aggregatedData': 'Aggregated Speed Statistics',
  'corner.lapSpeedTable': 'Lap Speed Breakdown',
  'corner.lapsAnalyzed': 'laps analyzed',
  'corner.noTracksWithCorners': 'No tracks with configured track shape and corners available.',
  'corner.noSessionsForTrack': 'No sessions found for track',
  'corner.noCornersFound': 'No corner annotations found for this track.',
  'corner.selectCorner': 'Select Corner',
  'corner.rank': 'Rank',
  'corner.driver': 'Driver',
  'corner.lap': 'Lap',
  'corner.lapTime': 'Lap Time',
  'corner.session': 'Session',
  'corner.speed': 'Speed',
  'corner.avgMax': 'Avg / Max',
  'corner.activeMetric': 'Active Metric:',
  'corner.metricSpeed': 'Speed',
  'corner.metricRpm': 'RPM',
  'corner.metricDuration': 'Duration',
  'corner.entranceRpm': 'Entrance RPM',
  'corner.apexRpm': 'Apex RPM',
  'corner.exitRpm': 'Exit RPM',
  'corner.rpmGain': 'RPM Gain',
  'corner.rpm': 'RPM',
  'corner.duration': 'Duration',
  'corner.transitDuration': 'Corner Transit Duration',
  'corner.bestDuration': 'Best Duration',
  'corner.avgDuration': 'Avg Duration',
  'corner.worstDuration': 'Slowest Duration',
  'corner.deltaToBestDuration': 'Δ to Best',
  'corner.fullTransit': 'Full Transit',



  // Sector analysis
  'sector.wizardChooseTrack': 'Choose Track',
  'sector.wizardDefine': 'Define Sectors',
  'sector.wizardSelect': 'Select Sessions',
  'sector.wizardResults': 'Results',
  'sector.title': 'Sector Analysis',
  'sector.defineSectors': 'Define Sectors',
  'sector.clickToAdd': 'Click to add · Drag to move (max 3 sectors)',
  'sector.selectSessions': 'Select Sessions →',
  'sector.analyzingLap': '⏳ Analyzing Lap…',
  'sector.analyzeLap': '⚡ Analyze Lap',
  'sector.noResults': 'No results available',
  'sector.noResultsHint': 'Go back and adjust your sectors or sessions',
  'sector.trackSectors': 'Track Sectors',
  'sector.sectorN': 'Sector {n}',
  'sector.noSessionsForTrack': 'No sessions found for track',
  'sector.failedToLoad': 'Failed to load sector analysis',
  'sector.lapStats': 'Lap Statistics',
  'sector.lapStatsDriver': 'Driver',
  'sector.lapStatsTotal': 'Total',

  // Sector panel
  'sectorPanel.time': 'Time',
  'sectorPanel.topSpeed': 'Top Speed',
  'sectorPanel.minSpeed': 'Min Speed',
  'sectorPanel.maxRpm': 'Max RPM',
  'sectorPanel.minRpm': 'Min RPM',
  'sectorPanel.duration': 'Duration',
  'sectorPanel.best': 'Best',
  'sectorPanel.avg': 'Avg',
  'sectorPanel.median': 'Median',
  'sectorPanel.worst': 'Worst',
  'sectorPanel.p10': 'P10',
  'sectorPanel.p5': 'P5',
  'sectorPanel.consistency': 'Consistency',
  'sectorPanel.lapByLap': 'Lap-by-Lap Timeline',
  'sectorPanel.sessionMedians': 'Session Medians',
  'sectorPanel.medianTopSpeed': 'Median Top Speed',
  'sectorPanel.medianMinSpeed': 'Median Min Speed',
  'sectorPanel.medianMaxRpm': 'Median Max RPM',
  'sectorPanel.medianMinRpm': 'Median Min RPM',
  'sectorPanel.medianTime': 'Median Time',
  'sectorPanel.session': 'Session',
  'sectorPanel.lap': 'Lap',
  'sectorPanel.maxSpeed': 'Max Speed',

  // Split points
  'splitPoints.clearAll': 'Clear All',

  // Session selector
  'sessionSelector.allDrivers': 'All Drivers',
  'sessionSelector.allWeather': 'All Weather',
  'sessionSelector.dryOnly': '☀️ Dry Only',
  'sessionSelector.wetOnly': '🌧️ Wet Only',
  'sessionSelector.selectAll': 'Select All',
  'sessionSelector.deselectAll': 'Deselect All',

  // Theoretical best
  'theoBest.title': 'Theoretical Best Lap',
  'theoBest.type': 'Type',
  'theoBest.time': 'Time',
  'theoBest.delta': 'Delta',
  'theoBest.theoretical': 'Theoretical',
  'theoBest.actualBest': 'Actual Best',

  // Lap selector
  'lapSelector.title': 'Select Laps',
  'lapSelector.loadingLaps': 'Loading laps…',
  'lapSelector.noLaps': 'No laps',
  'lapSelector.best': 'BEST',

  // Navigation – races
  'nav.races': 'Races',

  // Tracks
  'tracks.title': 'Tracks',
  'tracks.subtitle': 'All tracks & leaderboards',
  'tracks.noData': 'No tracks available',
  'tracks.failedToLoad': 'Failed to load tracks',
  'tracks.sessions': '{n} sessions',
  'tracks.raceSessions': '{n} race sessions',
  'tracks.bestLap': 'Best Lap',
  'tracks.rank': '#',
  'tracks.lapTime': 'Lap Time',
  'tracks.driver': 'Driver',
  'tracks.date': 'Date',
  'tracks.sectors': 'Sectors',
  'tracks.weather': 'Weather',
  'tracks.measuredLapsTab': 'Measured Laps',
  'tracks.raceTab': 'Races',
  'tracks.leaderboard': 'Leaderboard',
  'tracks.category': 'Category',
  'tracks.season': 'Season',
  'tracks.position': 'Pos.',
  'tracks.sessionType': 'Type',
  'tracks.sourceCanonical': 'Track',
  'tracks.sourceSession': 'Session only',
  'tracks.sourceRaceEvent': 'Race only',
  'tracks.searchPlaceholder': 'Search tracks…',
  'tracks.source': 'Source',
  'tracks.searchHelp.title': 'Track Search Syntax',
  'tracks.searchHelp.subtitle': 'Search by track name. Matching is case-insensitive and accent-insensitive.',
  'tracks.searchHelp.nameField': '(text)',
  'tracks.searchHelp.nameDesc': 'Matches tracks whose name contains the given text. Accented characters are matched regardless of accent marks (e.g. "Geneve" matches "Genève").',
  'tracks.searchHelp.example1': 'Wohlen',
  'tracks.searchHelp.example1Desc': 'All tracks containing "Wohlen"',
  'tracks.searchHelp.example2': 'franciacorta',
  'tracks.searchHelp.example2Desc': 'Case-insensitive match for "Franciacorta"',
  'tracks.searchHelp.example3': 'Sarno',
  'tracks.searchHelp.example3Desc': 'All tracks containing "Sarno"',

  // Track detail tabs
  'tracks.layoutTab': 'Layout',
  'tracks.noTrackLayout': 'No track layout available',
  'tracks.officialLapsTab': 'Official Laps',
  'tracks.noRaceEvents': 'No race events found',
  'tracks.round': 'Round',
  'tracks.championship': 'Championship',
  'tracks.dateStart': 'Date',
  'tracks.country': 'Country',
  'tracks.sessionsCount': 'Sessions',
  'tracks.driversCount': 'Drivers',
  // Track race events search
  'tracks.raceEvents.searchPlaceholder': 'Search race events…',
  'tracks.raceEvents.searchHelp.title': 'Race Events Search',
  'tracks.raceEvents.searchHelp.subtitle': 'Filter race events by season or championship name',
  'tracks.raceEvents.searchHelp.seasonField': 'season:<year>',
  'tracks.raceEvents.searchHelp.seasonDesc': 'Filter by season year (exact match)',
  'tracks.raceEvents.searchHelp.championshipField': 'championship:<name>',
  'tracks.raceEvents.searchHelp.championshipDesc': 'Filter by championship name (case-insensitive)',
  'tracks.raceEvents.searchHelp.example1': 'season:2026',
  'tracks.raceEvents.searchHelp.example1Desc': 'All events from the 2026 season',
  'tracks.raceEvents.searchHelp.example2': 'championship:RMC',
  'tracks.raceEvents.searchHelp.example2Desc': 'All events from championships containing "RMC"',

  // Track leaderboard search
  'tracks.leaderboard.searchPlaceholder': 'Search laps...',
  'tracks.leaderboard.searchHelp.title': 'Leaderboard Search',
  'tracks.leaderboard.searchHelp.subtitle': 'Use filters to narrow down results',
  'tracks.leaderboard.searchHelp.driverField': 'driver:<name>',
  'tracks.leaderboard.searchHelp.driverDesc': 'Filter by driver name (case-insensitive)',
  'tracks.leaderboard.searchHelp.seasonField': 'season:<year>',
  'tracks.leaderboard.searchHelp.seasonDesc': 'Filter by season year',
  'tracks.leaderboard.searchHelp.categoryField': 'category:<name>',
  'tracks.leaderboard.searchHelp.categoryDesc': 'Filter by category (e.g. DD2, Junior)',
  'tracks.leaderboard.searchHelp.typeField': 'type:<sessionType>',
  'tracks.leaderboard.searchHelp.typeDesc': 'Filter by race type (qualifying, heat-1, final...)',
  'tracks.leaderboard.searchHelp.posField': 'pos<:<N>',
  'tracks.leaderboard.searchHelp.posDesc': 'Max finish position (e.g. pos<:3 = podium)',
  'tracks.leaderboard.searchHelp.tempMinField': 'temp>:<N>',
  'tracks.leaderboard.searchHelp.tempMinDesc': 'Minimum temperature in °C',
  'tracks.leaderboard.searchHelp.tempMaxField': 'temp<:<N>',
  'tracks.leaderboard.searchHelp.tempMaxDesc': 'Maximum temperature in °C',
  'tracks.leaderboard.searchHelp.fromField': 'from:<YYYY-MM-DD>',
  'tracks.leaderboard.searchHelp.fromDesc': 'Start date filter',
  'tracks.leaderboard.searchHelp.toField': 'to:<YYYY-MM-DD>',
  'tracks.leaderboard.searchHelp.toDesc': 'End date filter',
  'tracks.leaderboard.searchHelp.example1': 'driver:gubser season:2026',
  'tracks.leaderboard.searchHelp.example1Desc': 'Find a driver in 2026 season',
  'tracks.leaderboard.searchHelp.example2': 'category:dd2 type:final',
  'tracks.leaderboard.searchHelp.example2Desc': 'DD2 finals only',
  'tracks.leaderboard.searchHelp.example3': 'pos<:3 from:2026-01-01',
  'tracks.leaderboard.searchHelp.example3Desc': 'Podium finishes in 2026',

  // Drivers
  'drivers.title': 'Drivers',
  'drivers.driver': 'Driver',
  'drivers.events': 'Events',
  'drivers.noData': 'No drivers found',
  'drivers.failedToLoad': 'Failed to load drivers',
  'drivers.searchPlaceholder': 'Search by name…',

  // Races
  'races.title': 'Race Results',
  'races.seasons': 'Seasons',
  'races.events': '{n} events',
  'races.categories': '{n} categories',
  'races.round': 'Round {n}',
  'races.allCategories': 'All Categories',
  'races.noEvents': 'No events found',
  'races.failedToLoad': 'Failed to load race data',
  'races.noData': 'No race data available',
  'races.position': 'Pos',
  'races.kart': '#',
  'races.driver': 'Driver',
  'races.bestLap': 'Best Lap',
  'races.totalTime': 'Total Time',
  'races.gap': 'Gap',
  'races.status': 'Status',
  'races.grid': 'Grid',
  'races.lap': 'Lap',
  'races.laps': 'Laps',
  'races.penalty': 'Penalty',
  'races.lapProgression': 'Lap Progression',
  'races.results': 'Results',
  'races.driverHistory': 'Driver History',
  'races.raceStarts': 'Starts',
  'races.podiums': 'Podiums',
  'races.dnfDsq': 'DNF / DSQ',
  'races.qualiPoles': 'Quali Poles',
  'races.track': 'Track',
  'races.session': 'Session',
  'races.year': 'Year',
  'races.category': 'Category',
  'races.pole': 'Pole',
  'races.winner': 'Winner',
  'races.finish': 'Finish',
  'races.sessionType.qualifying': 'Qualifying',
  'races.sessionType.heat-1': 'Heat 1',
  'races.sessionType.heat-2': 'Heat 2',
  'races.sessionType.heat-3': 'Heat 3',
  'races.sessionType.heat-ab': 'Heat A-B',
  'races.sessionType.heat-bc': 'Heat B-C',
  'races.sessionType.heat-ac': 'Heat A-C',
  'races.sessionType.final': 'Final',

  // Race overview
  'races.overview': 'Overview',

  // Driver race search
  'races.searchPlaceholder': 'Search races… e.g. Castelletto or type:race',
  'races.querySyntaxHelp': 'Query syntax help',
  'races.sessionCount': '{count} sessions',
  'races.sessionCountSingular': '1 session',
  'races.eventsFound': '{n} event(s) found',

  // Session detail tabs (below results table)
  'races.sessionTabWeather': 'Weather',
  'races.sessionTabPace': 'Pace',
  'common.comingSoon': 'Coming soon',

  // Driver pace
  'races.tabRaces': 'Races',
  'races.tabStats': 'Stats',
  'races.tabPace': 'Pace',
  'races.paceDescription': 'Fastest lap time per stage',
  'races.paceNoData': 'No pace data available for this category.',
  'races.paceEvents': '{n} events',
  'races.paceQualifying': 'Best lap time across all events',

  // Driver stats
  'races.statWins': 'Wins',
  'races.statAvgFinish': 'Avg. Finish Position',
  'races.statSeasons': 'Seasons',
  'races.statTracks': 'Tracks',
  'races.statCategories': 'Categories',
  'races.statTotalLaps': 'Total Laps',

  'driverSearchHelp.title': 'Race Search Syntax',
  'driverSearchHelp.subtitle': 'Filter race results using field:value pairs. Multiple terms are combined with AND.',
  'driverSearchHelp.bareText': '(bare text)',
  'driverSearchHelp.bareTextDesc': 'Matches track name or championship name containing the text.',
  'driverSearchHelp.track': 'track',
  'driverSearchHelp.trackDesc': 'Matches track name containing the value.',
  'driverSearchHelp.championship': 'championship',
  'driverSearchHelp.championshipDesc': 'Matches championship name containing the value.',
  'driverSearchHelp.category': 'category',
  'driverSearchHelp.categoryDesc': 'Matches category name containing the value.',
  'driverSearchHelp.type': 'type',
  'driverSearchHelp.typeDesc': 'Matches session type or label (e.g. race, qualifying, heat).',
  'driverSearchHelp.position': 'position',
  'driverSearchHelp.positionDesc': 'Filter by finish position. Supports =, >, >=, <, <= and ranges.',
  'driverSearchHelp.laps': 'laps',
  'driverSearchHelp.lapsDesc': 'Filter by completed laps. Supports comparison operators and ranges.',
  'driverSearchHelp.season': 'season',
  'driverSearchHelp.seasonDesc': 'Filter by championship season year. Supports comparison operators.',
  'driverSearchHelp.from': 'from',
  'driverSearchHelp.fromDesc': 'Races on or after a date. Accepts YYYY-MM-DD or relative: -7d, -2w, -3m, -1y.',
  'driverSearchHelp.to': 'to',
  'driverSearchHelp.toDesc': 'Races on or before a date. Same formats as "from".',
  'driverSearchHelp.operatorsTitle': 'Operators & Logic',
  'driverSearchHelp.numericComparisons': 'Numeric comparisons:',
  'driverSearchHelp.range': 'Range:',
  'driverSearchHelp.rangeDesc': '— matches values between 5 and 20 inclusive.',
  'driverSearchHelp.booleanLogic': 'Boolean logic:',
  'driverSearchHelp.booleanLogicDesc': 'Terms are implicitly joined with',
  'driverSearchHelp.quotes': 'Quotes:',
  'driverSearchHelp.quotesDesc': 'Wrap values with spaces in double quotes, e.g.',
  'driverSearchHelp.examplesTitle': 'Example Queries',
  'driverSearchHelp.example1Desc': 'Race results at Castelletto',
  'driverSearchHelp.example2Desc': 'RMC championship podiums from the last year',
  'driverSearchHelp.example3Desc': 'Final results in top 3',

  // Drivers List Search Help
  'driversListSearchHelp.title': 'Driver Search',
  'driversListSearchHelp.subtitle': 'Find drivers by name',
  'driversListSearchHelp.bareText': 'Keywords',
  'driversListSearchHelp.bareTextDesc': 'Keywords are matched against first name and last name. Multiple keywords are treated as AND conditions.',
  'driversListSearchHelp.from': 'from',
  'driversListSearchHelp.fromDesc': 'Drivers active on or after a date. Accepts YYYY-MM-DD or relative: -7d, -2w, -3m, -1y.',
  'driversListSearchHelp.to': 'to',
  'driversListSearchHelp.toDesc': 'Drivers active on or before a date. Same formats as "from".',
  'driversListSearchHelp.season': 'season',
  'driversListSearchHelp.seasonDesc': 'Drivers active in a specific championship season. Supports comparison operators like season:>2020.',
  'driversListSearchHelp.category': 'category',
  'driversListSearchHelp.categoryDesc': 'Drivers active in a specific category (e.g. Rotax Max).',
  'driversListSearchHelp.example.yanick': 'Find drivers named Yanick',
  'driversListSearchHelp.example.senn': 'Find drivers named Senn',
  'driversListSearchHelp.example.from': 'Active drivers from 2026',
  'driversListSearchHelp.example.season': 'Active drivers in 2025 or newer',
  'driversListSearchHelp.example.category': 'Active drivers in Rotax Max',
  'drivers.lastAppearance': 'Last Appearance',

  // Tyre Analysis
  'analysis.tyre.title': 'Tyre Analysis',
  'analysis.tyre.desc': 'Analyse tyre history and predict realistic lap times based on current conditions.',
  'tyre.selectTitle': 'Select Tyre',
  'tyre.selectHint': 'Showing your most recently used tyre sets. You can also scan a barcode.',
  'tyre.scanBarcode': 'Scan',
  'tyre.barcodeSearch': 'Search by barcode or name...',
  'tyre.fastestLaps': 'Fastest Laps by Track',
  'tyre.expectations': 'Realistic Expectations',
  'tyre.totalLaps': 'Total laps driven',
  'tyre.totalSessions': 'Sessions',
  'tyre.longestSession': 'Longest Session',
  'tyre.ageLaps': '{n} laps',
  'tyre.noData': 'No data',
  'tyre.expectedMedian': 'Expected',
  'tyre.matchingLaps': 'Matching laps',
  'tyre.noTyresFound': 'No tyre sets found.',
  'tyre.noFastestLaps': 'No laps recorded with this tyre set yet.',
  'tyre.noExpectations': 'No tracks with GPS data available for predictions.',
  'tyre.dry': 'Dry',
  'tyre.wet': 'Wet',
  'tyre.pointCamera': 'Point your camera at a barcode',
  'tyre.wizardSelect': 'Select Tyre',
  'tyre.wizardResults': 'Results',
  'tyre.back': 'Back',
  'tyre.exit': 'Exit',
  'tyre.trackColumn': 'Track',
  'tyre.bestLapColumn': 'Best Lap',
  'tyre.dateColumn': 'Date',
  'tyre.ageColumn': 'Age',
  'tyre.weatherColumn': 'Weather',
  'tyre.tempColumn': 'Temp',
  'tyre.expected': 'Expected:',

  // Tyre Overview
  'analysis.tyreOverview.title': 'Tyre Overview',
  'tyreOverview.summaryTotal': 'Total',
  'tyreOverview.summaryFresh': 'Fresh',
  'tyreOverview.summaryWorn': 'Worn',
  'tyreOverview.summaryEndOfLife': 'End of life',
  'tyreOverview.endOfLife': '⚠ End of life',
  'tyreOverview.scanButton': 'Scan',
  'tyreOverview.noTyres': 'No active tyres found.',
  'tyreOverview.scanNotFound': 'No tyre found for the scanned barcode.',
  'tyreOverview.laps': 'laps',
  'tyreOverview.colTyre': 'Tyre',
  'tyreOverview.colCompound': 'Compound',
  'tyreOverview.colLaps': 'Laps',
  'tyreOverview.colLastUsed': 'Last used',
  'tyreOverview.colStatus': 'Status',
  'tyreOverview.discardButton': 'Flag as discarded',
  'tyreOverview.discardAction': 'Discard',
  'tyreOverview.discardSuccess': 'Tyre set marked as discarded.',
  'tyreOverview.searchPlaceholder': 'Search tyres (e.g. brand:Mojo compound:dry laps:<50)...',
  'tyreOverview.searchHelp.title': 'Tyre Overview Search',
  'tyreOverview.searchHelp.subtitle': 'Filter and find active tyre sets by brand, compound, laps, barcode, or track.',
  'tyreOverview.searchHelp.bareText': 'Text',
  'tyreOverview.searchHelp.bareTextDesc': 'Matches brand, type, reference number, barcode, or last used track name.',
  'tyreOverview.searchHelp.brandDesc': 'Filter by tyre manufacturer or brand (e.g. Mojo, Vega).',
  'tyreOverview.searchHelp.typeDesc': 'Filter by tyre model or type (e.g. D2, White).',
  'tyreOverview.searchHelp.nrDesc': 'Filter by tyre set reference number.',
  'tyreOverview.searchHelp.compoundDesc': 'Filter by compound type ("dry" or "wet").',
  'tyreOverview.searchHelp.lapsDesc': 'Filter by cumulative laps driven with comparison operators (=, >, <, >=, <=) or ranges (e.g. 0..20).',
  'tyreOverview.searchHelp.barcodeDesc': 'Filter by individual tyre barcode/serial ID.',
  'tyreOverview.searchHelp.trackDesc': 'Filter by track name where the tyre set was last used.',
  'tyreOverview.searchHelp.fromDesc': 'Matches tyres last used on or after date (YYYY-MM-DD or relative like -30d).',
  'tyreOverview.searchHelp.toDesc': 'Matches tyres last used on or before date (YYYY-MM-DD or relative like -1m).',
  'tyreOverview.searchHelp.example.mojoDry': 'Dry compound tyres from Mojo',
  'tyreOverview.searchHelp.example.lapsEol': 'Tyres that have reached end-of-life (70+ laps)',
  'tyreOverview.searchHelp.example.lapsRange': 'Fresh or lightly used tyres with 0 to 20 laps',
  'tyreOverview.searchHelp.example.trackFrom': 'Tyres last used at Lonato since August 1st, 2026',
  'tyreOverview.searchHelp.example.vegaWet': 'Tyres by Vega or wet compound tyres',

  // Devices
  'device.alfano7': 'Alfano 7',
  'device.mychron5': 'MyChron 5',

  // Import
  'import.title': 'Import Sessions',
  'import.uploadZip': 'Upload Telemetry Files',
  'import.uploadZipDesc': 'Upload Alfano 7 (ZIP) or MyChron 5 (.xrk, .xrz, ZIP) telemetry data files from your computer',
  'import.dragDropZip': 'Drag & drop ZIP, XRK, or XRZ files here',
  'import.dropZipHere': 'Drop files here',
  'import.orBrowse': 'or',
  'import.browseFiles': 'browse files',
  'import.filesSelected': '{n} file(s) selected',
  'import.upload': 'Upload',
  'import.uploading': 'Uploading…',
  'import.uploadingPct': 'Uploading… {n}%',
  'import.imported': 'Imported',
  'import.skipped': 'Skipped',
  'import.errors': 'Errors',
  'import.errorDetailsTitle': 'Error details',
  'import.successMessage': 'Sessions imported successfully. They will appear in your dashboard.',
  'import.noNewSessions': 'No new sessions were found. All files were already imported.',
  'import.onlyZipAccepted': 'Only ZIP, XRK, and XRZ files are accepted',
  'import.onlySupportedAccepted': 'Only ZIP, XRK, and XRZ files are accepted',
  'import.uploadFailed': 'Upload failed (HTTP {status})',
  'import.connectionLost': 'Connection lost',
  'import.uploadAborted': 'Upload aborted',
  'import.importFailed': 'Import failed',
  'import.importedFromZip': '{n} session(s) imported',
  'import.zipImportFailed': 'Import failed: {error}',
  'import.useCurrentSetup': 'Use current setup',
  'offline.title': "You're Offline",
  'offline.message': 'You are currently not connected to the internet. Please check your network connection and try again.',
  'settings.uploadSessions': 'Upload Sessions',
  'settings.uploadSessionsDesc': 'Upload Alfano 7 or MyChron 5 telemetry files to import your session data.',
  // Sharing
  'sharing.title': 'Sharing',
  'sharing.searchPlaceholder': 'Search users by name or email...',
  'sharing.noUsersFound': 'No users found',
  'sharing.shareAll': 'Share all sessions',
  'sharing.shareSelected': 'Share selected only',
  'sharing.readOnly': 'Read-only',
  'sharing.readWrite': 'Read & Write',
  'sharing.share': 'Share',
  'sharing.myShares': 'My shares (outgoing)',
  'sharing.sharedWithMe': 'Shared with me (incoming)',
  'sharing.revoke': 'Revoke',
  'sharing.leave': 'Leave',
  'sharing.permissions': 'Permissions',
  'sharing.sharedAll': 'All sessions',
  'sharing.sharedSelected': 'Selected sessions',
  'sharing.searching': 'Searching…',
  'sharing.owner': 'Owner: {name}',
  'sharing.selectSessions': 'Select sessions',
  'sharing.noSessions': 'No sessions available',
  'sharing.laps': 'laps',
  'sharing.searchHint': 'Type at least 2 characters to search',
  'sharing.newShare': 'New Share',
  'sharing.stepUsers': 'Step 1: Select users',
  'sharing.stepConfigure': 'Step 2: Configure sharing',
  'sharing.stepPermissions': 'Step 3: Permissions',
  'sharing.next': 'Next',
  'sharing.selectedUsers': 'Selected users',
  'sharing.selectedSessions': '{count} session(s) selected',
  'sharing.filterSessions': 'Filter sessions...',
  'sharing.showSelected': 'Show selected',
  'sharing.noSharesYet': 'No shares yet',
  'sharing.emptyDescription': 'Share your sessions with other drivers or mechanics.',
  'sharing.stepScope': 'Step 1: Select sessions',
  'sharing.stepConfirm': 'Step 2: Confirm',
  'sharing.shareWithTeam': 'Share with this team',

  // Session tabs

  // Setup (readonly view)
  'setup.noSetup': 'No setup data available for this session.',
  'setup.loadingSetup': 'Loading setup…',
  'setup.loadFailed': 'Failed to load setup data.',
  'common.none': 'None',
  'common.notSet': 'Not set',
  'nav.setupSections': 'Setup Sections',
  // Pressure
  'pressure.tyre': 'Tyre Set',
  'pressure.outgoing': 'Outgoing',
  'pressure.cold': 'cold',
  'pressure.incoming': 'Incoming',
  'pressure.hot': 'hot',
  'pressure.fl': 'FL',
  'pressure.fr': 'FR',
  'pressure.rl': 'RL',
  'pressure.rr': 'RR',
  // Front
  'front.caster': 'Caster',
  'front.camber': 'Camber',
  'front.trackWidth': 'Track Width',
  'front.trackWidthUnit': 'mm',
  'front.rideHeight': 'Ride Height',
  'front.frontHubs': 'Front Hubs',
  'front.ackermann': 'Ackermann',
  'front.inner': 'Inner',
  'front.outer': 'Outer',
  'front.toeAlignment': 'Toe',
  'front.torsionBar': 'Torsion Bar',
  'front.hard': 'Hard',
  'front.medium': 'Medium',
  'front.soft': 'Soft',
  // Rear
  'rear.axle': 'Axle',
  'rear.rearWidth': 'Rear Width',
  'rear.rearHubs': 'Rear Hubs',
  'rear.loosenedBumper': 'Loosened rear bumper',
  // Engine
  'engine.engine': 'Engine',
  'engine.sparkPlug': 'Spark Plug',
  'engine.gearRatio': 'Gear Ratio',
  'engine.pinion': 'Pinion',
  'engine.crown': 'Crown',
  // Carburetor
  'carb.carburetor': 'Carburetor',
  'carb.mainJet': 'Main Jet',
  'carb.idleJet': 'Idle Jet',
  'carb.needleClip': 'Needle Clip',
  'carb.airScrew': 'Air Screw',
  'carb.floatHeight': 'Float Height',
  // Chassis
  'chassisSelect.chassis': 'Chassis',
  'chassisSelect.weight': 'Weight',
  // Notes
  'notes.notes': 'Notes',
  'notes.placeholder': 'Setup notes…',

  // Navigation – inventory
  'nav.inventory': 'Inventory',
  'nav.organization': 'Organization',
  'nav.setup': 'Setup',

  // Materials — common
  'common.add': 'Add',
  'common.save': 'Save',
  'common.create': 'Create',
  'common.created': 'Created',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.optional': '(optional)',
  'common.brand': 'Brand',
  'common.type': 'Type',
  'materials.profile': 'Profile',
  'materials.selectProfile': 'Select profile…',
  'common.nr': 'Nr#',
  'common.nrInfo': 'Sequential number to identify this entry.',
  'common.team': 'Team',
  'common.year': 'Year',
  'common.stiffness': 'Stiffness',
  'common.width': 'Width',
  'common.position': 'Position',

  // Materials — CRUD
  'crud.requiredField': '{field} is required',
  'crud.updated': '{entity} updated',
  'crud.created': '{entity} created',
  'crud.deleted': '{entity} deleted',
  'crud.confirmDelete': 'Delete {entity} "{name}"?\nThis cannot be undone.',
  'crud.emptyState': 'No {title} yet. Add your first {entity} above.',
  'crud.editEntity': 'Edit {entity}',
  'crud.newEntity': 'New {entity}',

  // Materials — general
  'materials.readOnly': 'Read-only',
  'materials.selectTeam': 'Select a team to view inventory.',

  // Materials — tabs
  'materials.tab.engines': 'Engines',
  'materials.tab.tyres': 'Tyres',
  'materials.tab.chassis': 'Chassis',
  'materials.tab.carburetors': 'Carburetors',
  'materials.tab.axles': 'Axles',
  'materials.tab.hubs': 'Hubs',
  'materials.tab.spark_plugs': 'Spark Plugs',

  // Materials — entities
  'materials.entity.engine': 'Engine',
  'materials.entity.tyre': 'Tyre',
  'materials.entity.chassis': 'Chassis',
  'materials.entity.carburetor': 'Carburetor',
  'materials.entity.axle': 'Axle',
  'materials.entity.hub': 'Hub',
  'materials.entity.spark_plug': 'Spark Plug',

  // Materials — tyre-specific
  'materials.tyre.compound': 'Compound',
  'materials.tyre.compound.dry': 'Dry',
  'materials.tyre.compound.wet': 'Wet',
  'materials.tyre.discarded': 'Discarded',

  // Materials — tyre IDs
  'materials.tyreId.title': 'Tire IDs',
  'materials.tyreId.fl': 'FL',
  'materials.tyreId.fr': 'FR',
  'materials.tyreId.rl': 'RL',
  'materials.tyreId.rr': 'RR',
  'materials.tyreId.scan': 'Scan barcode',
  'materials.tyreId.duplicateInSet': 'Duplicate tire ID within this set',
  'materials.tyreId.duplicateInOtherSet': 'ID {id} is already used by {tyre}',
  'materials.tyreId.placeholder': 'Scan or enter tire ID',
  'materials.tyreId.crossSetInfo': 'Some tire IDs conflict with other tyre sets. Press Save again to override.',

  // Materials — hub positions
  'materials.position.front': 'Front',
  'materials.position.rear': 'Rear',
  // Setup editing
  'setup.edit': 'Edit Setup',
  'setup.editTitle': 'Edit Setup',
  'setup.save': 'Save Setup',
  'setup.saving': 'Saving…',
  'setup.saved': 'Setup saved',
  'setup.saveFailed': 'Failed to save setup',
  'setup.pageTitle': 'Current Setup',
  'setup.pageSubtitle': 'Base configuration for newly imported sessions',
  'setup.editCurrentTitle': 'Edit Current Setup',
  'setup.noCurrentSetup': 'No setup configured yet. Click "Edit Setup" to get started.',
  // Propagation
  'propagation.title': 'Apply Changes',
  'propagation.subtitle': 'Apply setup changes to other sessions',
  'propagation.changes': 'Changes',
  'propagation.sessions': 'Sessions',
  'propagation.selectAll': 'Select all',
  'propagation.deselectAll': 'Deselect all',
  'propagation.apply': 'Apply',
  'propagation.applying': 'Applying…',
  'propagation.applied': 'Applied to sessions',
  'propagation.skip': 'Skip',
  'propagation.selectAtLeastOne': 'Select at least one session',
  'propagation.noMatching': 'No matching sessions found',
  // Propagation field labels
  'propagation.field.chassis': 'Chassis',
  'propagation.field.weight': 'Weight',
  'propagation.field.engine': 'Engine',
  'propagation.field.tyre': 'Tyre',
  'propagation.field.carburetor': 'Carburetor',
  'propagation.field.axle': 'Axle',
  'propagation.field.flOut': 'FL Out',
  'propagation.field.frOut': 'FR Out',
  'propagation.field.rlOut': 'RL Out',
  'propagation.field.rrOut': 'RR Out',
  'propagation.field.flIn': 'FL In',
  'propagation.field.frIn': 'FR In',
  'propagation.field.rlIn': 'RL In',
  'propagation.field.rrIn': 'RR In',
  'propagation.field.rearWidth': 'Rear Width',
  'propagation.field.frontHubs': 'Front Hubs',
  'propagation.field.rearHubs': 'Rear Hubs',
  'propagation.field.rearBumper': 'Rear Bumper',
  'propagation.field.caster': 'Caster',
  'propagation.field.camber': 'Camber',
  'propagation.field.trackWidth': 'Track Width',
  'propagation.field.rideHeight': 'Ride Height',
  'propagation.field.ackermann': 'Ackermann',
  'propagation.field.toe': 'Toe',
  'propagation.field.torsionBar': 'Torsion Bar',
  'propagation.field.frontSprocket': 'Pinion',
  'propagation.field.rearSprocket': 'Crown',
  'propagation.field.mainJet': 'Main Jet',
  'propagation.field.idleJet': 'Idle Jet',
  'propagation.field.needleClip': 'Needle Clip',
  'propagation.field.airScrew': 'Air Screw',
  'propagation.field.floatHeight': 'Float Height',
  'propagation.field.notes': 'Notes',
  'propagation.field.sparkPlug': 'Spark Plug',
  // Common additions
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.skip': 'Skip',
  'common.applying': 'Applying…',

  // Teams
  'nav.teams': 'Teams',
  'team.subtitle': 'Manage your teams and members',
  'team.create': 'Create Team',
  'team.name': 'Team Name',
  'team.members': 'Members',
  'team.role.read': 'Read',
  'team.role.write': 'Write',
  'team.role.manage': 'Manage',
  'team.role.owner': 'Owner',
  'team.addMember': 'Add Member',
  'team.removeMember': 'Remove Member',
  'team.leave': 'Leave Team',
  'team.confirmLeave': 'Are you sure you want to leave this team?',
  'team.cannotLeaveOwner': 'You cannot leave the team because you are the owner.',
  'team.created': 'Team created',
  'team.memberAdded': 'Member added',
  'team.memberRemoved': 'Member removed',
  'team.roleUpdated': 'Role updated',
  'team.renamed': 'Team renamed',
  'team.empty': 'No teams found. Create one to get started.',
  'team.noWriteAccess': 'You do not have write access to this team.',
  'team.tab.members': 'Members',
  'team.tab.sharing': 'Sharing',
  'team.members.searchPlaceholder': 'Search members...',
  'team.sharing.searchPlaceholder': 'Search shares...',
  'team.members.searchTitle': 'Search Members',
  'team.members.searchSubtitle': 'Filter members by name, email, or role',
  'team.sharing.searchTitle': 'Search Shares',
  'team.sharing.searchSubtitle': 'Filter shares by owner or type',
  'team.members.field.bareText': '(any text)',
  'team.members.field.bareTextDesc': 'Searches by name',
  'team.members.field.name': 'name:',
  'team.members.field.nameDesc': 'Filter by member name',
  'team.members.field.role': 'role:',
  'team.members.field.roleDesc': 'Filter by role (read, write, manage)',
  'team.sharing.field.bareText': '(any text)',
  'team.sharing.field.bareTextDesc': 'Searches owner name',
  'team.sharing.field.owner': 'owner:',
  'team.sharing.field.ownerDesc': 'Filter by share owner',
  'team.sharing.field.type': 'type:',
  'team.sharing.field.typeDesc': 'Filter by type (all, selected)',

  // Lap Diff Analysis
  'analysis.lapdiff.title': 'Lap Diff',
  'analysis.lapdiff.desc': 'Overlay two laps and highlight differences in speed, RPM, and acceleration.',
  'lapdiff.selectHint': 'Select exactly 2 laps to compare.',
  'lapdiff.compare': 'Compare',
  'lapdiff.comparing': 'Comparing…',
  'lapdiff.viewLapA': 'View Lap A',
  'lapdiff.viewLapB': 'View Lap B',
  'lapdiff.tabTime': 'Time',
  'lapdiff.tabSpeed': 'Speed',
  'lapdiff.tabRpm': 'RPM',
  'lapdiff.tabAcceleration': 'Acceleration',
  'lapdiff.timeChartTitle': 'Time Lost Over Track Position',
  'lapdiff.timeGained': 'Time Gained',
  'lapdiff.timeLost': 'Time Lost',
  'lapdiff.cumulativeDelta': 'Cumulative Δt',
  'lapdiff.segmentDelta': 'Segment Δt',
  'lapdiff.trackPosition': 'Track Position',
  'lapdiff.avgDiff': 'Avg Diff',
  'lapdiff.maxDiff': 'Max Diff',
  'lapdiff.noGpsData': 'No GPS data available for comparison.',
  'lapdiff.pctBetter': '% better',
  'lapdiff.segmentTime': 'Segment',
  'lapdiff.fullLap': 'Full Lap',
  'lapdiff.fasterBy': '{lap} faster by {time}',
  'lapdiff.equalTime': 'Equal time',
  'lapdiff.clearAnchors': 'Clear anchors',
  'lapdiff.clickToPlace': 'Click track to place anchors',
  'lapdiff.segmentRange': 'Segments #{start} – #{end}',
  'lapdiff.segmentDetail': 'Segment #{index}',

  // Aggregation Analysis
  'analysis.aggregation.title': 'Aggregation',
  'analysis.aggregation.desc': 'Aggregate braking, acceleration, and shift points across sessions on a track map.',
  'aggregation.brakingPoints': 'Braking',
  'aggregation.accelerationPoints': 'Acceleration',
  'aggregation.upshiftPoints': 'Upshift',
  'aggregation.top10Title': 'Top 10 Fastest Laps',
  'aggregation.noData': 'No data points detected',
  'aggregation.noDataHint': 'Try selecting different sessions.',
  'aggregation.trackShapeRequired': 'Track Shape Required',
  'aggregation.trackShapeRequiredHint': 'An official track shape is required to render aggregation box plots. Please create a track shape in the editor.',
  'aggregation.sessionSelectionHint': 'The more sessions included, the higher the quality and accuracy of the aggregated zones.',
  'aggregation.brakingSegment': 'Braking Zone',
  'aggregation.accelerationSegment': 'Acceleration Zone',
  'aggregation.upshiftSegment': 'Upshift Zone',
  'aggregation.tooltipSamples': ({ count }) => `${count} samples`,
  'aggregation.tooltipMedian': 'Median',
  'aggregation.tooltipSpread': 'Spread',
  'aggregation.gpsHeatmap': 'GPS Heat Map',
  'aggregation.eventZones': 'Event Zones',
  'aggregation.heatmapLow': 'Low',
  'aggregation.heatmapHigh': 'High',
  'aggregation.selectedLapLine': 'Selected Lap',
  'aggregation.lapsIncluded': '{count} laps',
  'gearshifting.wizardChooseTrack': 'Choose Track',
  'gearshifting.wizardSelect': 'Select Sessions',
  'gearshifting.wizardResults': 'Results',
  'gearshifting.analyzingLap': 'Analyzing…',

  // Aggregation Diff Analysis
  'analysis.aggregationdiff.title': 'Aggregation Diff',
  'analysis.aggregationdiff.desc': 'Compare two session aggregations to highlight differences in driving line, braking, acceleration, and shifting.',
  'aggregationdiff.groupA': 'Group A',
  'aggregationdiff.groupB': 'Group B',
  'aggregationdiff.baseline': 'Baseline',
  'aggregationdiff.comparison': 'Comparison',
  'aggregationdiff.groupLabelPlaceholder': 'Custom label (e.g. Morning Dry)',
  'aggregationdiff.loadSavedAggregation': 'Load from saved Aggregation…',
  'aggregationdiff.selectHint': 'Select sessions for Group A (Baseline) and Group B (Comparison) to highlight differences in driving line, braking, acceleration, and shifting.',
  'aggregationdiff.selectHintA': 'Configure Group A (Baseline) by selecting a single lap or one or more sessions.',
  'aggregationdiff.selectHintB': 'Configure Group B (Comparison) by selecting a single lap or one or more sessions to compare against Group A.',
  'aggregationdiff.compare': 'Compare Aggregations',
  'aggregationdiff.bestLap': 'Best Lap',
  'aggregationdiff.medianLap': 'Median Lap',
  'aggregationdiff.consistency': 'Consistency (σ)',
  'aggregationdiff.lapsAndSessions': '{laps} laps · {sessions} sessions',
  'aggregationdiff.layerDiffHeatmap': 'Line Diff Heatmap',
  'aggregationdiff.layerMedianLines': 'Median Lines',
  'aggregationdiff.layerEventDiffs': 'Event Zone Diffs',
  'aggregationdiff.hideUnchanged': 'Dim < 1.5m',
  'aggregationdiff.keyDifferencesTitle': 'Key Differences',
  'aggregationdiff.keyDifferencesSubtitle': 'Track ranges ranked by time difference, with possible reasons. Click any range to focus on the map.',
  'aggregationdiff.filterAll': 'All',
  'aggregationdiff.filterLine': 'Driving Line',
  'aggregationdiff.filterBraking': 'Braking',
  'aggregationdiff.filterAcceleration': 'Acceleration',
  'aggregationdiff.filterUpshift': 'Upshift',
  'aggregationdiff.filterTopSpeed': 'Top Speed',
  'aggregationdiff.possibleReasons': 'Possible Reasons',
  'aggregationdiff.topSpeedPeak': 'Peak {a} vs {b} km/h',
  'aggregationdiff.laterBy': '{group} {dist}m later',
  'aggregationdiff.earlierBy': '{group} {dist}m earlier',
  'aggregationdiff.widerBy': 'Spread +{dist}m wider',
  'aggregationdiff.tighterBy': 'Spread {dist}m tighter',
  'aggregationdiff.onlyInGroup': 'Only in {group}',
  'aggregationdiff.lineSeparation': '{dist}m max line separation ({len}m zone)',
  'aggregationdiff.lineOffsetLeft': 'Left +{dist}m',
  'aggregationdiff.lineOffsetRight': 'Right +{dist}m',
  'aggregationdiff.noSignificantDiffs': 'No significant differences found for this filter.',
  'aggregationdiff.sharedLine': 'Shared Line',
  'aggregationdiff.clearFocus': 'Clear Selection',
  'aggregationdiff.avgSpeed': 'Avg Speed',
  'aggregationdiff.speedDiff': 'Speed Diff',
  'aggregationdiff.lineOffset': 'Line Offset',
  'aggregationdiff.layerFasterSegments': 'Faster Segments',
  'aggregationdiff.fasterSegmentLegend': 'Faster Segment (Speed)',
  'aggregationdiff.estTimeGained': '{group} faster by {time}',
  'aggregationdiff.estTimeLost': '{group} faster by {time}',
  'aggregationdiff.estTimeNeutral': 'Est. ±0.00s',

  // Analysis History
  'analysis.history.title': 'Analysis History',
  'analysis.history.subtitle': 'Reopen and manage previously evaluated telemetry analyses',
  'analysis.history.filterAll': 'All Tools',
  'analysis.history.filterSector': 'Sector',
  'analysis.history.filterCorner': 'Corner',
  'analysis.history.filterBraking': 'Braking',
  'analysis.history.filterLapDiff': 'Lap Diff',
  'analysis.history.filterAggregation': 'Aggregation',
  'analysis.history.filterAggregationDiff': 'Aggregation Diff',
  'analysis.history.groupsScope': '{a} vs {b} session(s)',
  'analysis.history.empty': 'No saved analyses found',
  'analysis.history.emptyDesc': 'Analyses you run while logged in will automatically appear here.',
  'analysis.history.open': 'Open',
  'analysis.history.rename': 'Rename',
  'analysis.history.delete': 'Delete',
  'analysis.history.deleteConfirm': 'Are you sure you want to delete this analysis from history?',
  'analysis.history.deleteSuccess': 'Analysis deleted',
  'analysis.history.renameSuccess': 'Analysis renamed',
  'analysis.history.searchPlaceholder': 'Search analysis history...',
  'analysis.history.loading': 'Loading analyses...',
  'analysis.history.table.type': 'Type',
  'analysis.history.table.name': 'Name',
  'analysis.history.table.track': 'Track',
  'analysis.history.table.scope': 'Scope',
  'analysis.history.table.created': 'Created',
  'analysis.history.table.actions': 'Actions',
  'analysis.history.total': '{n} total',
  'analysis.history.sessionsCount': '{n} session(s)',
  'analysis.history.lapsCount': '{n} lap(s)',
  'analysis.history.viewAll': 'View Full History',
  'analysis.sessionRemoved.title': 'Session Removed',
  'analysis.sessionRemoved.description': 'The telemetry session used by this analysis has been deleted and this analysis cannot be opened anymore.',
  'analysis.sessionRemoved.deleteAnalysis': 'Delete Analysis',
  'analysis.sessionRemoved.close': 'Close',

  /* ── Footer ──── */
  'footer.contact': 'Contact',
  'footer.privacyPolicy': 'Privacy Policy',
  'footer.reportBug': 'Report a Bug',
  'footer.bugReportTitle': 'Report a Bug',
  'footer.description': 'Description',
  'footer.descriptionPlaceholder': 'Describe the issue you encountered...',
  'footer.submit': 'Submit',
  'footer.submitting': 'Submitting...',
  'footer.submitted': 'Bug report submitted. Thank you!',
  'footer.submitFailed': 'Failed to submit bug report.',

  /* ── Error auto-reporting & Error Boundary ─────────────────────────── */
  'error.autoReported': 'An error occurred and has been automatically reported.',
  'errorBoundary.title': 'Something went wrong',
  'errorBoundary.message': 'An unexpected error occurred and has been reported to the server.',
  'errorBoundary.reload': 'Reload Page',
  'errorBoundary.reset': 'Try Again',
  'errorBoundary.showDetails': 'Show error details',
  'errorBoundary.hideDetails': 'Hide error details',

  /* ── Setup Coach ──────────────────────────────────────────── */
  'coach.title': 'Setup Coach',
  'coach.subtitle': 'A knowledge base of kart setup adjustments organised by driving goal. Select a problem to see recommended changes and their effects.',
  'coach.disclaimer': 'These recommendations are generic guidelines for Rotax Max karts. Always test changes incrementally and adjust based on track conditions, driver feedback, and tyre state.',
  'coach.col.parameter': 'Parameter',
  'coach.col.change': 'Change',
  'coach.col.effect': 'Effect',
  'coach.col.sideEffects': 'Side Effects',
  'coach.col.effectiveness': 'Effectiveness',

  // Reduce Understeer
  'coach.goal.understeer.title': 'Reduce Understeer',
  'coach.goal.understeer.desc': 'The kart pushes wide through corners and does not follow the intended racing line. The front tyres are losing grip before the rear, making the kart feel unresponsive to steering inputs.',
  'coach.goal.understeer.adj1.param': 'Front Track Width',
  'coach.goal.understeer.adj1.change': 'Widen',
  'coach.goal.understeer.adj1.effect': 'More front mechanical grip',
  'coach.goal.understeer.adj1.side': 'May increase front aggressiveness',
  'coach.goal.understeer.adj2.param': 'Front Caster',
  'coach.goal.understeer.adj2.change': 'Increase',
  'coach.goal.understeer.adj2.effect': 'More front grip on turn-in',
  'coach.goal.understeer.adj2.side': 'Less straight-line stability',
  'coach.goal.understeer.adj3.param': 'Front Camber',
  'coach.goal.understeer.adj3.change': 'Add (more negative)',
  'coach.goal.understeer.adj3.effect': 'More front grip mid-corner',
  'coach.goal.understeer.adj3.side': 'Uneven tyre wear',
  'coach.goal.understeer.adj4.param': 'Front Ride Height',
  'coach.goal.understeer.adj4.change': 'Lower',
  'coach.goal.understeer.adj4.effect': 'Lower centre of gravity, more grip',
  'coach.goal.understeer.adj4.side': 'Risk of bottoming out',
  'coach.goal.understeer.adj5.param': 'Rear Axle',
  'coach.goal.understeer.adj5.change': 'Softer (less stiff)',
  'coach.goal.understeer.adj5.effect': 'Frees up rear to rotate',
  'coach.goal.understeer.adj5.side': 'May cause rear instability',
  'coach.goal.understeer.adj6.param': 'Front Tyre Pressure',
  'coach.goal.understeer.adj6.change': 'Increase slightly',
  'coach.goal.understeer.adj6.effect': 'Sharper front response',
  'coach.goal.understeer.adj6.side': 'Reduced contact patch',

  // Reduce Oversteer
  'coach.goal.oversteer.title': 'Reduce Oversteer',
  'coach.goal.oversteer.desc': 'The rear of the kart slides out through corners, making the kart rotate too aggressively. The rear tyres are losing grip before the front, leading to snap oversteer or a loose rear end.',
  'coach.goal.oversteer.adj1.param': 'Front Track Width',
  'coach.goal.oversteer.adj1.change': 'Narrow',
  'coach.goal.oversteer.adj1.effect': 'Less front grip, balances handling',
  'coach.goal.oversteer.adj1.side': 'Less steering response',
  'coach.goal.oversteer.adj2.param': 'Front Caster',
  'coach.goal.oversteer.adj2.change': 'Reduce',
  'coach.goal.oversteer.adj2.effect': 'Less front grip',
  'coach.goal.oversteer.adj2.side': 'Slower turn-in',
  'coach.goal.oversteer.adj3.param': 'Rear Axle',
  'coach.goal.oversteer.adj3.change': 'Stiffen',
  'coach.goal.oversteer.adj3.effect': 'More rear grip',
  'coach.goal.oversteer.adj3.side': 'Chassis may hop on bumps',
  'coach.goal.oversteer.adj4.param': 'Rear Width',
  'coach.goal.oversteer.adj4.change': 'Widen',
  'coach.goal.oversteer.adj4.effect': 'More rear mechanical grip',
  'coach.goal.oversteer.adj4.side': 'Less rear rotation',
  'coach.goal.oversteer.adj5.param': 'Rear Tyre Pressure',
  'coach.goal.oversteer.adj5.change': 'Reduce slightly',
  'coach.goal.oversteer.adj5.effect': 'More rear contact patch',
  'coach.goal.oversteer.adj5.side': 'Slower tyre warm-up',
  'coach.goal.oversteer.adj6.param': 'Front Torsion Bar',
  'coach.goal.oversteer.adj6.change': 'Add',
  'coach.goal.oversteer.adj6.effect': 'Stiffens front, reduces front grip',
  'coach.goal.oversteer.adj6.side': 'Less front compliance',

  // Chassis Bounce
  'coach.goal.bounce.title': 'Chassis Bounces Through Corners',
  'coach.goal.bounce.desc': 'The chassis hops or bounces through corners instead of maintaining smooth contact with the track. This is typically caused by the chassis being too stiff relative to the grip level, making it impossible to carry consistent speed through corners.',
  'coach.goal.bounce.adj1.param': 'Rear Axle',
  'coach.goal.bounce.adj1.change': 'Softer (less stiff)',
  'coach.goal.bounce.adj1.effect': 'Absorbs energy, smooths ride',
  'coach.goal.bounce.adj1.side': 'May reduce rear grip',
  'coach.goal.bounce.adj2.param': 'Rear Width',
  'coach.goal.bounce.adj2.change': 'Widen',
  'coach.goal.bounce.adj2.effect': 'Stabilises chassis through corner',
  'coach.goal.bounce.adj2.side': 'Less rotation on exit',
  'coach.goal.bounce.adj3.param': 'Rear Bumper',
  'coach.goal.bounce.adj3.change': 'Loosen',
  'coach.goal.bounce.adj3.effect': 'Adds flex, absorbs vibration',
  'coach.goal.bounce.adj3.side': 'Less rigid support',
  'coach.goal.bounce.adj4.param': 'Rear Tyre Pressure',
  'coach.goal.bounce.adj4.change': 'Reduce',
  'coach.goal.bounce.adj4.effect': 'More compliance over bumps',
  'coach.goal.bounce.adj4.side': 'Slower response',
  'coach.goal.bounce.adj5.param': 'Front Ride Height',
  'coach.goal.bounce.adj5.change': 'Raise',
  'coach.goal.bounce.adj5.effect': 'More chassis clearance',
  'coach.goal.bounce.adj5.side': 'Less front grip',

  // Reduce Front Aggressiveness
  'coach.goal.frontAggressive.title': 'Reduce Front Aggressiveness',
  'coach.goal.frontAggressive.desc': 'The front end has too much grip, causing the kart to turn in too sharply and unsettle the rear. The kart feels nervous on corner entry and may snap into oversteer.',
  'coach.goal.frontAggressive.adj1.param': 'Front Track Width',
  'coach.goal.frontAggressive.adj1.change': 'Narrow',
  'coach.goal.frontAggressive.adj1.effect': 'Reduces front grip',
  'coach.goal.frontAggressive.adj1.side': 'More understeer tendency',
  'coach.goal.frontAggressive.adj2.param': 'Front Caster',
  'coach.goal.frontAggressive.adj2.change': 'Reduce',
  'coach.goal.frontAggressive.adj2.effect': 'Less front bite on turn-in',
  'coach.goal.frontAggressive.adj2.side': 'Slower turn-in',
  'coach.goal.frontAggressive.adj3.param': 'Front Torsion Bar',
  'coach.goal.frontAggressive.adj3.change': 'Remove or reduce',
  'coach.goal.frontAggressive.adj3.effect': 'Softens front end',
  'coach.goal.frontAggressive.adj3.side': 'Front may lack precision',
  'coach.goal.frontAggressive.adj4.param': 'Front Camber',
  'coach.goal.frontAggressive.adj4.change': 'Reduce (less negative)',
  'coach.goal.frontAggressive.adj4.effect': 'Less cornering grip',
  'coach.goal.frontAggressive.adj4.side': 'More even tyre wear',
  'coach.goal.frontAggressive.adj5.param': 'Front Tyre Pressure',
  'coach.goal.frontAggressive.adj5.change': 'Increase',
  'coach.goal.frontAggressive.adj5.effect': 'Smaller contact patch',
  'coach.goal.frontAggressive.adj5.side': 'Sharper but less grip',

  // Reduce Rear Sliding
  'coach.goal.rearSlide.title': 'Reduce Rear Sliding',
  'coach.goal.rearSlide.desc': 'The rear of the kart slides excessively during cornering, causing time loss and excessive tyre wear. The kart lacks rear traction and feels unstable under lateral load.',
  'coach.goal.rearSlide.adj1.param': 'Rear Axle',
  'coach.goal.rearSlide.adj1.change': 'Stiffen',
  'coach.goal.rearSlide.adj1.effect': 'More rear grip',
  'coach.goal.rearSlide.adj1.side': 'May hop on bumps',
  'coach.goal.rearSlide.adj2.param': 'Rear Width',
  'coach.goal.rearSlide.adj2.change': 'Widen',
  'coach.goal.rearSlide.adj2.effect': 'More rear mechanical grip',
  'coach.goal.rearSlide.adj2.side': 'Less free rotation',
  'coach.goal.rearSlide.adj3.param': 'Rear Tyre Pressure',
  'coach.goal.rearSlide.adj3.change': 'Increase slightly',
  'coach.goal.rearSlide.adj3.effect': 'Better tyre shape under load',
  'coach.goal.rearSlide.adj3.side': 'Reduced contact patch at extremes',
  'coach.goal.rearSlide.adj4.param': 'Front Track Width',
  'coach.goal.rearSlide.adj4.change': 'Narrow',
  'coach.goal.rearSlide.adj4.effect': 'Balances grip distribution',
  'coach.goal.rearSlide.adj4.side': 'Less front bite',
  'coach.goal.rearSlide.adj5.param': 'Rear Hubs',
  'coach.goal.rearSlide.adj5.change': 'Use wider',
  'coach.goal.rearSlide.adj5.effect': 'More rear leverage',
  'coach.goal.rearSlide.adj5.side': 'Heavier steering feel',

  // Corner Exit Traction
  'coach.goal.cornerExit.title': 'Improve Corner Exit Traction',
  'coach.goal.cornerExit.desc': 'The kart struggles to put power down on corner exit, with the rear tyres spinning or sliding when accelerating out of slow corners. This causes time loss on the exit-to-straight phase.',
  'coach.goal.cornerExit.adj1.param': 'Rear Width',
  'coach.goal.cornerExit.adj1.change': 'Widen',
  'coach.goal.cornerExit.adj1.effect': 'More mechanical grip on exit',
  'coach.goal.cornerExit.adj1.side': 'Less mid-corner rotation',
  'coach.goal.cornerExit.adj2.param': 'Rear Axle',
  'coach.goal.cornerExit.adj2.change': 'Stiffen',
  'coach.goal.cornerExit.adj2.effect': 'Plants rear on acceleration',
  'coach.goal.cornerExit.adj2.side': 'May hop under load',
  'coach.goal.cornerExit.adj3.param': 'Rear Tyre Pressure',
  'coach.goal.cornerExit.adj3.change': 'Lower',
  'coach.goal.cornerExit.adj3.effect': 'More contact patch on exit',
  'coach.goal.cornerExit.adj3.side': 'Slower tyre warm-up',
  'coach.goal.cornerExit.adj4.param': 'Rear Sprocket',
  'coach.goal.cornerExit.adj4.change': 'Increase (more teeth)',
  'coach.goal.cornerExit.adj4.effect': 'More low-end torque',
  'coach.goal.cornerExit.adj4.side': 'Lower top speed',
  'coach.goal.cornerExit.adj5.param': 'Rear Bumper',
  'coach.goal.cornerExit.adj5.change': 'Loosen',
  'coach.goal.cornerExit.adj5.effect': 'More flex under load',
  'coach.goal.cornerExit.adj5.side': 'Less predictable',

  // Hot Conditions
  'coach.goal.hotConditions.title': 'Adapt to Hot Conditions',
  'coach.goal.hotConditions.desc': 'Track temperature is high, causing increased grip levels and tyre overheating. The chassis may feel too grippy, leading to hopping or front aggressiveness. Engine runs hotter and richer mixture may be needed.',
  'coach.goal.hotConditions.adj1.param': 'All Tyre Pressures',
  'coach.goal.hotConditions.adj1.change': 'Increase',
  'coach.goal.hotConditions.adj1.effect': 'Compensates for heat expansion',
  'coach.goal.hotConditions.adj1.side': 'May reduce grip if too high',
  'coach.goal.hotConditions.adj2.param': 'Main Jet',
  'coach.goal.hotConditions.adj2.change': 'Richen (larger number)',
  'coach.goal.hotConditions.adj2.effect': 'Prevents lean condition',
  'coach.goal.hotConditions.adj2.side': 'Slight power loss',
  'coach.goal.hotConditions.adj3.param': 'Rear Axle',
  'coach.goal.hotConditions.adj3.change': 'Soften',
  'coach.goal.hotConditions.adj3.effect': 'Compensates for increased grip',
  'coach.goal.hotConditions.adj3.side': 'May feel loose',
  'coach.goal.hotConditions.adj4.param': 'Rear Width',
  'coach.goal.hotConditions.adj4.change': 'Narrow slightly',
  'coach.goal.hotConditions.adj4.effect': 'Reduces excess rear grip',
  'coach.goal.hotConditions.adj4.side': 'Less exit traction',
  'coach.goal.hotConditions.adj5.param': 'Air Screw',
  'coach.goal.hotConditions.adj5.change': 'Open (more turns out)',
  'coach.goal.hotConditions.adj5.effect': 'Richer idle mixture',
  'coach.goal.hotConditions.adj5.side': 'May affect idle quality',

  // Cold/Wet Conditions
  'coach.goal.coldConditions.title': 'Adapt to Cold / Wet Conditions',
  'coach.goal.coldConditions.desc': 'Track temperature is low or the surface is wet, causing reduced grip levels. The kart needs maximum mechanical grip and engine jetting must compensate for denser air. Make all changes conservatively and prioritise driver safety.',
  'coach.goal.coldConditions.adj1.param': 'All Tyre Pressures',
  'coach.goal.coldConditions.adj1.change': 'Reduce',
  'coach.goal.coldConditions.adj1.effect': 'Compensates for cold contraction',
  'coach.goal.coldConditions.adj1.side': 'Risk of tyre rolling on rim',
  'coach.goal.coldConditions.adj2.param': 'Main Jet',
  'coach.goal.coldConditions.adj2.change': 'Lean (smaller number)',
  'coach.goal.coldConditions.adj2.effect': 'Compensates for denser air',
  'coach.goal.coldConditions.adj2.side': 'Risk of overheating engine',
  'coach.goal.coldConditions.adj3.param': 'Rear Axle',
  'coach.goal.coldConditions.adj3.change': 'Stiffen',
  'coach.goal.coldConditions.adj3.effect': 'Generates more mechanical grip',
  'coach.goal.coldConditions.adj3.side': 'May hop on wet surface',
  'coach.goal.coldConditions.adj4.param': 'Front & Rear Width',
  'coach.goal.coldConditions.adj4.change': 'Widen both',
  'coach.goal.coldConditions.adj4.effect': 'Maximises mechanical grip',
  'coach.goal.coldConditions.adj4.side': 'More aggressive handling',
  'coach.goal.coldConditions.adj5.param': 'Air Screw',
  'coach.goal.coldConditions.adj5.change': 'Close (fewer turns out)',
  'coach.goal.coldConditions.adj5.effect': 'Leaner idle mixture',
  'coach.goal.coldConditions.adj5.side': 'May affect cold starting',

  // Braking Stability
  'coach.goal.braking.title': 'Improve Braking Stability',
  'coach.goal.braking.desc': 'The kart is unstable under braking, with the rear stepping out or the front locking up. This makes it difficult to brake late and consistently, costing time on corner entry.',
  'coach.goal.braking.adj1.param': 'Front Tyre Pressure',
  'coach.goal.braking.adj1.change': 'Increase',
  'coach.goal.braking.adj1.effect': 'More precise front under braking',
  'coach.goal.braking.adj1.side': 'Reduced braking contact patch',
  'coach.goal.braking.adj2.param': 'Front Caster',
  'coach.goal.braking.adj2.change': 'Increase',
  'coach.goal.braking.adj2.effect': 'More trail, stabilises under braking',
  'coach.goal.braking.adj2.side': 'Heavier steering',
  'coach.goal.braking.adj3.param': 'Front Ride Height',
  'coach.goal.braking.adj3.change': 'Lower',
  'coach.goal.braking.adj3.effect': 'More front weight transfer',
  'coach.goal.braking.adj3.side': 'Risk of bottoming out',
  'coach.goal.braking.adj4.param': 'Front Torsion Bar',
  'coach.goal.braking.adj4.change': 'Add',
  'coach.goal.braking.adj4.effect': 'Stiffens front end under braking',
  'coach.goal.braking.adj4.side': 'May reduce turn-in grip',
  'coach.goal.braking.adj5.param': 'Rear Tyre Pressure',
  'coach.goal.braking.adj5.change': 'Reduce',
  'coach.goal.braking.adj5.effect': 'Prevents rear locking',
  'coach.goal.braking.adj5.side': 'Slower rear response',

  // Engine Tuning
  'coach.goal.engine.title': 'Fine-Tune Engine Performance',
  'coach.goal.engine.desc': 'Optimise engine output through gearing and carburetor adjustments. These changes affect power delivery, acceleration, top speed, and engine safety. Always monitor EGT (exhaust gas temperature) when changing jetting.',
  'coach.goal.engine.adj1.param': 'Rear Sprocket',
  'coach.goal.engine.adj1.change': 'Increase (more teeth)',
  'coach.goal.engine.adj1.effect': 'More acceleration, lower top speed',
  'coach.goal.engine.adj1.side': 'Higher RPM on straights',
  'coach.goal.engine.adj2.param': 'Rear Sprocket',
  'coach.goal.engine.adj2.change': 'Decrease (fewer teeth)',
  'coach.goal.engine.adj2.effect': 'Higher top speed, less acceleration',
  'coach.goal.engine.adj2.side': 'Lower RPM, slower exits',
  'coach.goal.engine.adj3.param': 'Main Jet',
  'coach.goal.engine.adj3.change': 'Richen (larger number)',
  'coach.goal.engine.adj3.effect': 'Cooler EGT, safer',
  'coach.goal.engine.adj3.side': 'Slight power loss',
  'coach.goal.engine.adj4.param': 'Main Jet',
  'coach.goal.engine.adj4.change': 'Lean (smaller number)',
  'coach.goal.engine.adj4.effect': 'More power',
  'coach.goal.engine.adj4.side': 'Risk of seizure, higher EGT',
  'coach.goal.engine.adj5.param': 'Needle Clip',
  'coach.goal.engine.adj5.change': 'Raise position',
  'coach.goal.engine.adj5.effect': 'Leaner mid-range, more power',
  'coach.goal.engine.adj5.side': 'Risk of lean condition',
  'coach.goal.engine.adj6.param': 'Float Height',
  'coach.goal.engine.adj6.change': 'Adjust',
  'coach.goal.engine.adj6.effect': 'Controls fuel level in bowl',
  'coach.goal.engine.adj6.side': 'Affects all RPM ranges',

  // Track Map Controls
  'trackMap.zoomIn': 'Zoom In',
  'trackMap.zoomOut': 'Zoom Out',
  'trackMap.fitTrack': 'Fit Track',
  'trackMap.satellite': 'Satellite',
  'trackMap.vector': 'Vector',
  'trackMap.switchProvider': 'Switch aerial imagery provider',

  // Lap Analysis Queue & Amended Analysis
  'queue.processingLaps': 'Analyzing laps {done}/{total}',
  'queue.completed': 'Lap analysis complete ({total}/{total})',
  'tracks.reanalyzeLaps': 'Re-analyze Laps',
  'tracks.reanalyzingLaps': 'Queueing…',
  'tracks.reanalyzeQueued': 'Queued {enqueued} lap(s) for analysis ({skipped} already up-to-date).',
  'tracks.reanalyzeAllUpToDate': 'All {skipped} lap(s) are already up-to-date with current track data.',
  'lapInfo.canonicalSectors': 'Track Sectors',
  'lapInfo.loggerSectors': 'Logger Sectors',
  'lapAnalysis.amendedCornersTitle': 'Corner & Braking Breakdown',
  'lapAnalysis.cornerCol': 'Corner',
  'lapAnalysis.topApproachSpeed': 'Top Speed Before Braking',
  'lapAnalysis.slowestSpeed': 'Slowest Speed',
  'lapAnalysis.apexSpeed': 'Apex Speed',
  'lapAnalysis.exitSpeed': 'Exit Speed',
  'lapAnalysis.brakingDist': 'Braking Dist.',
  'lapAnalysis.cornerTime': 'Duration',
  'lapAnalysis.statusCompleted': 'Analysis completed',
  'lapAnalysis.statusPending': 'Analysis pending',
  'session.tabCorners': 'Corners',
  'session.noCornersConfigured': 'No corner analysis data available for this session.',
  'lapAnalysis.entrySpeed': 'Entry Speed',
  'lapAnalysis.trailBraking': 'Trail Braking',

  // Terms & Conditions
  'footer.termsAndConditions': 'Terms & Conditions',
  'terms.gateTitle': 'Terms & Conditions',
  'terms.gateSubtitle': 'Please review and accept the latest Terms & Conditions to continue.',
  'terms.updatedBannerTitle': 'Updated Terms & Conditions:',
  'terms.updatedBannerDesc': 'Our Terms & Conditions have been updated. Please review and accept the new version to continue using kart.win.',
  'terms.checkboxLabel': 'I have read and agree to the Terms & Conditions',
  'terms.acceptButton': 'Accept & Continue',
  'terms.acceptingButton': 'Accepting…',
  'terms.loadError': 'Unable to load the Terms & Conditions. Please check your connection and try again.',
  'terms.acceptError': 'Failed to record your acceptance. Please try again.',

  // Additional Common & Import Keys
  'common.previous': 'Previous',
  'common.remove': 'Remove',
  'common.success': 'Saved successfully',
  'common.failedToSave': 'Failed to save',
  'import.useCurrentSetupDesc': 'Associate the current active kart setup with the imported session(s).',

  // Search & Filter Bar
  'search.booleanLogicUse': 'Combine terms with',
  'search.booleanLogicOr': 'or',
  'search.booleanLogicGroup': 'and group expressions with parentheses',
  'filter.operator.eq': 'is',
  'filter.operator.neq': 'is not',
  'filter.operator.gt': 'greater than',
  'filter.operator.gte': 'at least',
  'filter.operator.lt': 'less than',
  'filter.operator.lte': 'at most',
  'filter.operator.range': 'between',
  'filter.operator.contains': 'contains',
  'filter.remove': 'Remove filter',
  'filter.done': 'Done',
  'filter.clearInput': 'Clear input',
  'filter.selectField': 'Filter by field',
  'filter.pressEnter': 'Press Enter to apply',
  'filter.selectValues': 'Select values',
  'filter.noMatchingOptions': 'No matching options',

  // Search Schemas
  'filter.session.bareSearchExample': 'Search by track name',
  'filter.session.searchTrack': 'Filter by track name',
  'filter.session.filterLaps': 'Filter by total lap count',
  'filter.session.filterBestTime': 'Filter by best lap time (seconds)',
  'filter.session.filterMaxSpeed': 'Filter by top speed (km/h)',
  'filter.session.filterMaxRpm': 'Filter by peak engine RPM',
  'filter.session.filterDate': 'Filter by session date',
  'filter.session.filterShared': 'Filter by team sharing status',
  'filter.session.filterVisibility': 'Filter by session ownership or team visibility',
  'filter.session.visibilityMine': 'My Sessions',
  'filter.session.visibilityShared': 'Shared with Team',
  'filter.session.exampleTrackLaps': 'Sessions at Lonato with 10+ laps',
  'filter.session.exampleBestSpeed': 'Fast laps with high top speed',

  'filter.analysis.bareSearchExample': 'Search by analysis title or track name',
  'filter.analysis.searchTitle': 'Filter by analysis title',
  'filter.analysis.searchTrack': 'Filter by track name',
  'filter.analysis.filterType': 'Filter by analysis type',
  'filter.analysis.filterDate': 'Filter by creation date',
  'filter.analysis.filterShared': 'Filter by team sharing status',
  'filter.analysis.filterVisibility': 'Filter by ownership or team sharing',
  'filter.analysis.exampleCornerTrack': 'Corner analyses at Lonato',
  'filter.analysis.exampleSharedDate': 'Shared analyses from 2026',

  'filter.trackRaces.bareSearchExample': 'Search by event title, season, or category',
  'filter.trackRaces.searchTitle': 'Filter by event title',
  'filter.trackRaces.searchSeason': 'Filter by championship season',
  'filter.trackRaces.searchCategory': 'Filter by racing category',
  'filter.trackRaces.filterRound': 'Filter by round number',
  'filter.trackRaces.filterDate': 'Filter by event date',
  'filter.trackRaces.exampleCatDate': 'ROK Mini events in 2026',
  'filter.trackRaces.exampleSeasonRound': 'First two rounds of the ROK Cup',

  'filter.tracks.bareSearchExample': 'Search by track name or country',
  'filter.tracks.searchName': 'Filter by track name',
  'filter.tracks.searchCountry': 'Filter by country code',
  'filter.tracks.filterType': 'Filter by track source (official or measured)',
  'filter.tracks.filterSessions': 'Filter by number of recorded sessions',
  'filter.tracks.filterLaps': 'Filter by number of recorded laps',
  'filter.tracks.filterEvents': 'Filter by number of official race events',
  'filter.tracks.filterDrivers': 'Filter by number of official drivers',
  'filter.tracks.exampleItSessions': 'Italian tracks with recorded sessions',
  'filter.tracks.exampleOfficialEvents': 'Official circuits with 3+ race events',

  'filter.leaderboard.bareSearchExample': 'Search by driver, category, season, or event',
  'filter.leaderboard.searchDriver': 'Filter by driver name',
  'filter.leaderboard.searchCategory': 'Filter by racing category',
  'filter.leaderboard.searchSeason': 'Filter by championship season',
  'filter.leaderboard.searchEvent': 'Filter by event name',
  'filter.leaderboard.filterLapTime': 'Filter by lap time (seconds)',
  'filter.leaderboard.filterSpeed': 'Filter by top speed (km/h)',
  'filter.leaderboard.filterRpm': 'Filter by max RPM',
  'filter.leaderboard.filterDate': 'Filter by lap date',
  'filter.leaderboard.exampleTimeSpeed': 'Sub-48s laps with high top speed',
  'filter.leaderboard.exampleCatTime': 'Mini category laps between 52s and 54s',

  'filter.tyres.bareSearchExample': 'Search by tyre name, brand, model, or barcode',
  'filter.tyres.searchName': 'Filter by tyre set name',
  'filter.tyres.searchBrand': 'Filter by tyre brand',
  'filter.tyres.searchModel': 'Filter by tyre compound/model',
  'filter.tyres.searchSize': 'Filter by tyre size',
  'filter.tyres.searchBarcode': 'Filter by scanned barcode',
  'filter.tyres.filterStatus': 'Filter by lifecycle status',
  'filter.tyres.filterCondition': 'Filter by remaining tread condition (%)',
  'filter.tyres.filterLaps': 'Filter by total laps driven',
  'filter.tyres.filterDistance': 'Filter by total distance driven (km)',
  'filter.tyres.exampleActiveBrand': 'Active LeCont tyre sets',
  'filter.tyres.exampleConditionLaps': 'Tyres with >50% life and <30 laps',

  'filter.teamMembers.bareSearchExample': 'Search by member name or email',
  'filter.teamMembers.searchName': 'Filter by member name',
  'filter.teamMembers.searchEmail': 'Filter by member email',
  'filter.teamMembers.filterRole': 'Filter by team role',
  'filter.teamMembers.exampleRoleAdmin': 'Team administrators',
  'filter.teamMembers.exampleNameRole': 'Members or editors matching name',

  'filter.teamSharing.bareSearchExample': 'Search by title, owner, or track',
  'filter.teamSharing.searchTitle': 'Filter by item title',
  'filter.teamSharing.searchOwner': 'Filter by owner name',
  'filter.teamSharing.searchTrack': 'Filter by track name',
  'filter.teamSharing.filterKind': 'Filter by shared item type',
  'filter.teamSharing.kindSession': 'Session',
  'filter.teamSharing.kindAnalysis': 'Analysis',
  'filter.teamSharing.filterAccess': 'Filter by access permission',
  'filter.teamSharing.accessRead': 'Read Only',
  'filter.teamSharing.accessWrite': 'Read & Write',
  'filter.teamSharing.exampleSessionTrack': 'Shared sessions from Lonato',
  'filter.teamSharing.exampleAnalysisWrite': 'Editable shared analyses',

  'filter.drivers.bareSearchExample': 'Search by driver name, team, category, or equipment',
  'filter.drivers.searchName': 'Filter by driver name',
  'filter.drivers.searchCountry': 'Filter by nationality code',
  'filter.drivers.searchTeam': 'Filter by racing team',
  'filter.drivers.searchCategory': 'Filter by racing category',
  'filter.drivers.searchChassis': 'Filter by chassis brand',
  'filter.drivers.searchEngine': 'Filter by engine brand',
  'filter.drivers.filterEntries': 'Filter by race entry count',
  'filter.drivers.exampleCountryEntries': 'Italian drivers with 5+ race entries',
  'filter.drivers.exampleCatTeam': 'Mini drivers for a specific team',

  'filter.driverHistory.bareSearchExample': 'Search by season, event, track, or category',
  'filter.driverHistory.searchSeason': 'Filter by championship season',
  'filter.driverHistory.searchEvent': 'Filter by event name',
  'filter.driverHistory.searchTrack': 'Filter by circuit name',
  'filter.driverHistory.searchCategory': 'Filter by category',
  'filter.driverHistory.searchTeam': 'Filter by team name',
  'filter.driverHistory.filterPos': 'Filter by finishing position',
  'filter.driverHistory.filterGrid': 'Filter by starting grid position',
  'filter.driverHistory.filterPoints': 'Filter by championship points scored',
  'filter.driverHistory.filterBestLap': 'Filter by fastest lap time (seconds)',
  'filter.driverHistory.filterPenalties': 'Filter by penalty count',
  'filter.driverHistory.examplePodiums': 'Podium finishes (Top 3)',
  'filter.driverHistory.examplePointsPenalty': 'High-scoring events without penalties',

  // Additional Analysis & Component Keys
  'braking.colSelect': 'Sel',
  'braking.colCompare': 'Cmp',
  'braking.scoreUnit': '/ 100',
  'braking.trackOverview': 'Track Overview',
  'braking.cornersCount': '{count} corners',
  'braking.trackAnalysis': 'Track Braking Analysis',
  'braking.lapsTotal': '{count} laps total',
  'braking.sample': 'Sample',
  'lapdiff.pointDetail': 'Point Detail',
  'lapdiff.selectTwoLaps': 'Select 2 laps',
  'lapdiff.lapTime': 'Lap Time',
  'aggregationdiff.toggleLayers': 'Toggle Map Layers',
  'aggregationdiff.leftInner': 'Left (Inner)',
  'aggregationdiff.rightOuter': 'Right (Outer)',
  'aggregationdiff.selectLap': 'Select a lap',
  'aggregationdiff.modeSingleLap': 'Single Lap',
  'aggregationdiff.modeSessions': 'Sessions',
  'braking.phaseBraking': 'Braking',
  'braking.phaseAccel': 'Acceleration',
  'setup.tabFront': 'Front',
  'setup.tabRear': 'Rear',
  'team.you': 'You',
  'races.entries': 'Entries',
  // Filter Schema & UI Translations
  'filter.filterBy': 'Filter by {label}',
  'filter.removeFilter': 'Remove {label} filter',
  'filter.prevMonth': 'Previous month',
  'filter.nextMonth': 'Next month',
  'filter.selectStartDate': 'Select start date in calendar',
  'filter.op.exact': 'Exact',
  'filter.op.atLeast': 'At least',
  'filter.op.atMost': 'At most',
  'filter.op.between': 'Between',
  'filter.op.greater': 'Greater',
  'filter.op.less': 'Less',
  'filter.preset.today': 'Today',
  'filter.preset.last7d': 'Last 7 days',
  'filter.preset.last30d': 'Last 30 days',
  'filter.preset.last90d': 'Last 90 days',
  'filter.preset.last1y': 'Last 1 year',
  'filter.preset.last7dShort': 'Last 7d',
  'filter.preset.last30dShort': 'Last 30d',
  'filter.preset.last90dShort': 'Last 90d',
  'filter.preset.last1yShort': 'Last 1y',
  'filter.sessionType.race': 'Race',
  'filter.sessionType.final': 'Final',
  'filter.sessionType.pre_final': 'Pre-Final',
  'filter.sessionType.heat': 'Heat',
  'filter.sessionType.qualifying': 'Qualifying',
  'filter.unit.laps': 'laps',
  'filter.schema.tyres.title': 'Tyre Search Syntax',
  'filter.schema.tyres.subtitle': 'Filter tyres by brand, compound, usage, barcode, track, or discarded status.',
  'filter.field.brand': 'Brand',
  'filter.desc.brand': 'Tyre brand manufacturer (Mojo, Vega, LeCont, Bridgestone, etc.)',
  'filter.field.tyreModel': 'Tyre Model',
  'filter.placeholder.tyreModel': 'e.g. D2, D5, W5, CIK Option',
  'filter.desc.tyreModel': 'Model name or compound specification',
  'filter.field.compound': 'Compound',
  'filter.desc.compound': 'Tyre compound category (dry, wet, slick, rain)',
  'filter.compound.dry': 'Dry',
  'filter.compound.wet': 'Wet',
  'filter.compound.slick': 'Slick',
  'filter.compound.rain': 'Rain',
  'filter.field.setNumber': 'Set Number',
  'filter.desc.setNumber': 'Tyre set number assigned in inventory',
  'filter.field.totalLaps': 'Total Laps',
  'filter.desc.totalLaps': 'Cumulative total laps run on this tyre set',
  'filter.preset.tyreFresh': 'Fresh (0..15 laps)',
  'filter.preset.tyreBrokenIn': 'Broken-in (16..50 laps)',
  'filter.preset.tyreHighMileage': 'High Mileage (> 50 laps)',
  'filter.field.barcode': 'Barcode',
  'filter.placeholder.barcode': 'FL, FR, RL, RR barcode',
  'filter.desc.barcode': 'Partial match on any tyre barcode within the set',
  'filter.field.lastTrack': 'Last Track',
  'filter.desc.lastTrack': 'Last track where this tyre set was used',
  'filter.field.discarded': 'Discarded',
  'filter.desc.discarded': 'Whether the tyre set has been marked as discarded',
  'filter.discarded.false': 'No',
  'filter.discarded.true': 'Yes',
  'filter.example.activeTyres': 'Active (non-discarded) tyre sets',
  'filter.example.dryMojo': 'Dry Mojo tyres',
  'filter.example.highMileage70': 'High mileage tyre sets over 70 laps',
  'filter.example.freshTyres': 'Fresh / low mileage tyres',
  'filter.example.lonatoTyres': 'Tyres used at Lonato since August 2026',
  'filter.example.vegaOrWet': 'Vega sets or any wet tyres',
  'filter.field.date': 'Date',
  'filter.desc.date': 'Filter by relative date preset (-7d, -30d, today) or specific calendar date range',
  'filter.field.track': 'Track',
  'filter.desc.track': 'Matches track name',
  'filter.placeholder.track': 'e.g. Wohlen, Lonato',
  'filter.field.driver': 'Driver',
  'filter.desc.driver': 'Matches driver display name',
  'filter.placeholder.driver': 'e.g. Yanick',
  'filter.field.season': 'Season',
  'filter.desc.season': 'Matches season year',
  'filter.field.category': 'Category',
  'filter.desc.category': 'Canonical racing category',
  'filter.field.championship': 'Championship',
  'filter.desc.championship': 'Championship series name',
  'filter.field.sessionType': 'Session Type',
  'filter.desc.sessionType': 'Session classification (race, heat, qualifying, final)',
  'filter.field.position': 'Position',
  'filter.field.finishPosition': 'Finish Position',
  'filter.desc.position': 'Finish position (e.g. position:<=3 for podiums)',
  'filter.preset.posWinner': 'Winner (P1)',
  'filter.preset.posPodium': 'Podium (≤ P3)',
  'filter.preset.posTop5': 'Top 5 (≤ P5)',
  'filter.preset.posTop10': 'Top 10 (≤ P10)',
  'filter.field.temp': 'Temperature',
  'filter.desc.temp': 'Ambient session temperature in Celsius (e.g. temp:15..25)',
  'filter.preset.tempCool': 'Cool (< 15°C)',
  'filter.preset.tempMild': 'Mild (15..25°C)',
  'filter.preset.tempWarm': 'Warm (≥ 25°C)',
  'filter.field.laps': 'Laps',
  'filter.desc.laps': 'Matches completed lap count (e.g. laps:>=5, laps:5..20)',
  'filter.preset.lapsShort': 'Short (≤ 5 laps)',
  'filter.preset.lapsMedium': 'Medium (6..15 laps)',
  'filter.preset.lapsLong': 'Long (> 15 laps)',
  'analysis.sector': 'Sector Analysis',
  'analysis.corner': 'Corner Speed',
  'analysis.braking': 'Braking Analysis',
  'analysis.lapdiff': 'Lap Diff',
  'analysis.aggregation': 'Aggregation',
  'analysis.aggregationdiff': 'Aggregation Diff',
  'filter.schema.analyses.title': 'Analysis Search Syntax',
  'filter.schema.analyses.subtitle': 'Filter analysis history using structured keys or free-text keywords.',
  'filter.field.nameTrack': 'Name / Track',
  'filter.placeholder.nameTrack': 'e.g. Sarno, Custom Run',
  'filter.desc.nameTrack': 'Matches analysis name or track name (unaccented, partial match)',
  'filter.field.toolType': 'Tool Type',
  'filter.placeholder.toolType': 'e.g. sector, corner, lapdiff, aggregation, aggregationdiff',
  'filter.desc.toolType': 'Filter by analysis tool type (sector, corner, lapdiff, aggregation, aggregationdiff)',
  'filter.example.sarnoSector': 'Sector analyses at Sarno',
  'filter.example.lapdiff30d': 'Lap diff analyses from the last 30 days',
  'filter.example.wohlenCorner': 'Corner analyses at Wohlen or Mirecourt',
  'filter.schema.sessions.title': 'Session Search Syntax',
  'filter.schema.sessions.subtitle': 'Filter sessions using structured keys or free-text keywords.',
  'filter.field.trackDriver': 'Track / Driver',
  'filter.placeholder.trackDriver': 'e.g. Wohlen, Mirecourt',
  'filter.desc.trackDriver': 'Matches track display name or driver name (unaccented, partial match)',
  'filter.schema.driverHistory.title': 'Driver History Search Syntax',
  'filter.schema.driverHistory.subtitle': 'Filter career race history by track, championship, category, session type, position, or season.',
  'filter.example.podiumWohlen': 'Podium finishes at Wohlen',
  'filter.example.dd2Races2026': '2026 DD2 races',
  'filter.example.victoriesLast1y': 'Race victories in the last year',
  'filter.schema.driversList.title': 'Drivers Search Syntax',
  'filter.schema.driversList.subtitle': 'Filter drivers by name, active season, category, or participation date.',
  'filter.field.driverName': 'Driver Name',
  'filter.placeholder.driverName': 'e.g. Yanick, Senn',
  'filter.desc.driverName': 'Matches driver first or last name (partial, case-insensitive)',
  'filter.example.findDriverYanick': 'Find drivers matching Yanick',
  'filter.example.activeDd22026': 'Active DD2 drivers in 2026',
  'filter.example.driversLast90d': 'Drivers who raced in the last 90 days',
  'filter.schema.teamMembers.title': 'Team Members Search Syntax',
  'filter.schema.teamMembers.subtitle': 'Filter team members by name, assigned role, or email.',
  'filter.field.memberName': 'Member Name',
  'filter.placeholder.memberName': 'e.g. Yanick, Alice',
  'filter.desc.memberName': 'Matches member display name',
  'filter.field.role': 'Role',
  'filter.desc.role': 'Permission level (admin, manage, read)',
  'team.role.admin': 'Admin',
  'filter.field.email': 'Email',
  'filter.placeholder.email': 'e.g. gmail.com',
  'filter.desc.email': 'Matches email address',
  'filter.example.findMemberYanick': 'Find members matching Yanick',
  'filter.example.findTeamAdmins': 'Find team administrators',
  'filter.example.findTeamManage': 'Find members with manage permissions',
  'filter.schema.teamSharing.title': 'Team Sharing Search Syntax',
  'filter.schema.teamSharing.subtitle': 'Filter shared access by owner, share type, or permission level.',
  'filter.field.ownerName': 'Owner / Name',
  'filter.desc.ownerName': 'Matches owner display name',
  'filter.field.shareScope': 'Share Scope',
  'filter.desc.shareScope': 'Sharing scope type (all data, single session, track only)',
  'filter.shareScope.all': 'All Telemetry',
  'filter.shareScope.single': 'Single Session',
  'filter.shareScope.track': 'Specific Track',
  'filter.example.sharesYanick': 'Find shares from Yanick',
  'filter.example.sharesAllRead': 'Read-only full telemetry shares',
  'filter.schema.trackLeaderboard.title': 'Track Leaderboard Search Syntax',
  'filter.schema.trackLeaderboard.subtitle': 'Filter leaderboard laps and sessions by driver, season, category, position, or temperature.',
  'filter.example.yanickFastest2026': 'Yanick\'s fastest laps in the 2026 season',
  'filter.example.podiumSeniorMax': 'Podium finishes in Senior MAX',
  'filter.example.warm90d': 'Warm track sessions in the last 90 days',
  'filter.schema.trackRaceEvents.title': 'Race Events Search Syntax',
  'filter.schema.trackRaceEvents.subtitle': 'Filter race events by season, championship, category, or participating driver.',
  'filter.desc.driverEvent': 'Matches driver participating in the event',
  'filter.example.rmc2026': '2026 RMC Switzerland events',
  'filter.example.yanick2025': 'Events where Yanick participated in 2025',
  'filter.example.dd2Last1y': 'DD2 events in the last year',
  'filter.schema.tracks.title': 'Track Search Syntax',
  'filter.schema.tracks.subtitle': 'Filter tracks by name or keywords.',
  'filter.field.trackName': 'Track Name',
  'filter.example.findWohlen': 'Find tracks matching Wohlen',
  'filter.example.exactTrack': 'Exact track name match',
};
